/**
 * Compliance Audit and Monitoring Work Plan — Compliance Oversight Activities
 * CMS CPE Protocol Universe Table 1 layout. Template-based dropdowns.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import useImpersonation from '../../hooks/useImpersonation';
import useAgentProgress from '../../lib/useAgentProgress';
import AgentThinkingPanel from '../../components/AgentThinkingPanel';
import MeasurementsModal from './MeasurementsModal';
import COARecordModal from './COARecordModal';
import ActivityTable from './ActivityTable';
import ChartingBoard from './ChartingBoard';
import { SetupWizard } from './MonitoringBoard';
import RolloverDialog from './RolloverDialog';

const API = process.env.REACT_APP_API_URL || '';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const CMS_DATE_RE = /^\d{4}\/\d{2}\/\d{2}$/;

function pad2(n) {
  return String(n).padStart(2, '0');
}

// Readable paragraph cell for prose columns (rationale, description,
// deficiencies, remediation): the FULL text, always visible, line breaks
// preserved. No clamp, no hover tooltip, no click-to-expand — operator
// requirement 2026-07-20: "the paragraph must be able to read completely."
function ProseCell({ text }) {
  return <span className="block break-words whitespace-pre-line leading-relaxed">{text}</span>;
}

function isoToCmsDate(iso) {
  if (!iso || typeof iso !== 'string') return '';
  const s = iso.trim();
  if (CMS_DATE_RE.test(s)) return s;
  if (ISO_DATE_RE.test(s)) return s.replaceAll('-', '/');
  // Date-carrying timestamps (e.g. '2026-03-01T00:00:00.000Z' from a DATE
  // column) must keep their CALENDAR date. Routing them through new Date()
  // re-renders UTC midnight in the viewer's timezone, which showed
  // March 1 as February 28 — a start date "on the last day of the month".
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return s.slice(0, 10).replaceAll('-', '/');
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}`;
}

function cmsToIsoDate(cms) {
  if (!cms || typeof cms !== 'string') return null;
  const s = cms.trim();
  if (ISO_DATE_RE.test(s)) return s;
  if (!CMS_DATE_RE.test(s)) return null;
  return s.replaceAll('/', '-');
}

function formatCmsDateTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function useFetch(url, opts = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const refresh = useCallback(() => {
    if (!url) return;
    setLoading(true);
    setError('');
    fetch(url, { credentials: 'include', ...opts })
      .then((r) => r.json())
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => { setError(e.message); setLoading(false); });
  }, [url]);
  useEffect(() => { refresh(); }, [refresh]);
  return { data, loading, error, refresh };
}

const TAB_CREATE = 'create';
const TAB_UNIVERSE = 'universe';
const TAB_MONITORING = 'monitoring';
const TAB_NOTIFICATIONS = 'notifications';

// Column definitions: readable titles, tooltips, default width (px). Used by Create form and Universe table.
// Default widths set so full labels fit without truncation; user can resize columns in both Create and Universe.
const COA_COLUMNS = [
  { key: 'component', label: 'A Component', tooltip: 'Component or area being audited (e.g. Claims, Enrollment).', defaultWidth: 160 },
  { key: 'entity_name', label: 'Entity name', tooltip: 'Name of the FDR (First Tier Entity) or internal department.', defaultWidth: 200 },
  { key: 'departments', label: 'Departments', tooltip: 'One or more departments responsible for this activity. Select all that apply.', defaultWidth: 220 },
  { key: 'activity_type', label: 'B Activity type', tooltip: 'Type of activity: Auditing, Monitoring, or Investigations.', defaultWidth: 160 },
  { key: 'compliance_or_fwa', label: 'C Compliance or FWA', tooltip: 'Compliance, FWA (Fraud, Waste, Abuse), or Both.', defaultWidth: 180 },
  { key: 'activity_frequency', label: 'D Frequency', tooltip: 'How often the activity occurs (e.g. Quarterly, Monthly, Ad-hoc). Monthly, quarterly, and semiannual activities are created for each remaining period of the plan year automatically.', defaultWidth: 180 },
  { key: 'activity_quarter', label: 'Quarter', tooltip: 'Quarter when the audit or monitoring takes place (Q1–Q4).', defaultWidth: 100 },
  { key: 'activity_rationale', label: 'E Rationale', tooltip: 'Rationale for including this activity in the work plan.', defaultWidth: 320 },
  { key: 'activity_description', label: 'F Activity description', tooltip: 'Description of the oversight activity.', defaultWidth: 380 },
  { key: 'activity_start_date', label: 'G Start date', tooltip: 'Planned or actual start date.', defaultWidth: 130 },
  { key: 'activity_completion_date', label: 'H Completion date', tooltip: 'Planned or actual completion date.', defaultWidth: 150 },
  { key: 'number_of_deficiencies', label: 'I Number of deficiencies', tooltip: 'Count of deficiencies identified.', defaultWidth: 120 },
  { key: 'description_of_deficiencies', label: 'J Description of deficiencies', tooltip: 'Summary of deficiencies found. Use NA or TBD if none.', defaultWidth: 240 },
  { key: 'remediation_activities', label: 'Remediation activities', tooltip: 'For monitoring/SIU: metric, threshold. For auditing: what was done to correct the deficiency.', defaultWidth: 240 },
  // The standard the activity is measured against. Syncable measures carry
  // theirs from the annual plan wizard; this is where every other activity
  // states its own, so a result can be judged instead of assumed.
  { key: 'benchmark', label: 'Benchmark', tooltip: 'The standard this activity is measured against, for example 95% timely, or 2 minutes as 120 seconds. Leave blank and the period passes only when there are no deficiencies.', defaultWidth: 190 },
  // Quantitative results. Not a CMS universe column (the CPE layout is A-L); it
  // reads from the coa_activity_metrics child table and never affects the export.
  { key: 'metrics_summary', label: 'Measurements', tooltip: 'Quantitative result for this activity, broken out by case type. Click to see each case type and set a target.', defaultWidth: 170 },
  { key: 'corrective_action_required', label: 'K Corrective action required?', tooltip: 'Whether corrective action is required: Y, N, or TBD.', defaultWidth: 220 },
  { key: 'activity_results_shared', label: 'L Results shared?', tooltip: 'Whether results were shared and with whom (e.g. Y + committee name).', defaultWidth: 180 },
  { key: 'cfr_references', label: 'CFR references', tooltip: 'Relevant CFR references (e.g. 42 CFR 422, 423).', defaultWidth: 140 },
  { key: 'chapter_references', label: 'Chapter references', tooltip: 'Relevant chapter references (e.g. MA Chapter 2).', defaultWidth: 140 }
];

const EXTRA_UNIVERSE_COLUMNS = [
  { key: 'status', label: 'Status', tooltip: 'Draft, in progress, or completed.', defaultWidth: 110 },
  { key: 'created_cap_id', label: 'CAP', tooltip: 'CAP ID if one was created from this activity.', defaultWidth: 100 },
  { key: 'rebuttal_requested_at', label: 'Rebuttal requested', tooltip: 'Date when rebuttal was requested from department owner.', defaultWidth: 130 },
  { key: 'actions', label: 'Actions', tooltip: 'View activity or request rebuttal from department owner.', defaultWidth: 140 }
];

/**
 * Compact measurement cell, shared by the work plan grid and the universe view.
 *
 * Shows the overall timeliness rate for activities that carry measurements and
 * an em dash for those that do not, which is every activity except those fed by
 * a metric source. Clicking opens the per-case-type detail.
 *
 * Module scope, not a closure: both call sites live in sibling components
 * (WorkActivitiesTable and UniverseView), so the open handler arrives as a prop
 * the same way onViewReport already does.
 */
function renderMetricsCell(act, onOpen) {
  const s = act.metrics_summary;
  if (!s || !s.count) return <span className="text-slate-400">—</span>;
  const rate = s.rate === null || s.rate === undefined ? null : Number(s.rate);
  // Benchmark language only. The old cell printed raw untimely-case counts
  // ("81 untimely") next to activities whose deficiency rule is benchmark
  // met = 0 / missed = 1 — case detail belongs in the measurements modal,
  // never in the summary a reviewer scans.
  const tone = s.below_target > 0 ? 'text-rose-700' : 'text-emerald-700';
  const label = `${act.component || 'Activity'}${act.entity_name ? ` — ${act.entity_name}` : ''}`;
  return (
    <button
      type="button"
      onClick={() => onOpen && onOpen({ id: act.id, name: label })}
      disabled={!onOpen}
      className="group text-left disabled:cursor-default"
      title="View each case type and set a target"
    >
      <span className={`block text-sm font-semibold tabular-nums ${tone} ${onOpen ? 'group-hover:underline' : ''}`}>
        {rate === null ? 'no cases' : `${rate}% timely`}
      </span>
      <span className="block text-xs text-slate-500">
        {s.count} case type{s.count === 1 ? '' : 's'}
        {s.below_target > 0 ? `, benchmark missed` : ''}
      </span>
    </button>
  );
}

// Sync a Precision COA row. Module sources (privacy, issues log, CAP,
// Reporter) score directly — one source of truth, nothing to choose. Scrubber
// One click closes a SCORED month: status completed, completion date
// stamped, row locked (the engine refuses further syncs and manual results
// on completed rows). Only offered when the row is met/missed and open.
function CloseMonthButton({ act, onDone }) {
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const close = async () => {
    setBusy(true); setErr(null);
    try {
      const res = await fetch(`${API}/api/coa/activities/${act.id}/close-month`, {
        method: 'PATCH', credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.message || 'Could not close the month.');
      onDone && onDone();
    } catch (e) {
      setErr(e.message);
      setBusy(false);
    }
  };
  return (
    <span className="inline-flex items-center gap-1">
      <button type="button" onClick={close} disabled={busy}
        className="text-emerald-600 hover:text-emerald-700 font-medium text-xs whitespace-nowrap disabled:opacity-50"
        title="Close this month: mark the scored result final and lock the row">
        {busy ? 'Closing…' : 'Close month'}
      </button>
      {err && <span className="text-[10px] text-rose-600" title={err}>!</span>}
    </span>
  );
}

// The mirror of Close month: a closed row returns to in progress, its
// completion date clears, and it unlocks for syncs and results. Offered only
// on completed rows (they live in the Closed view).
function ReopenButton({ act, onDone }) {
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const reopen = async () => {
    if (!window.confirm('Reopen this activity? It returns to In progress and unlocks for results.')) return;
    setBusy(true); setErr(null);
    try {
      const res = await fetch(`${API}/api/coa/activities/${act.id}/reopen`, {
        method: 'PATCH', credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.message || 'Could not reopen the activity.');
      onDone && onDone();
    } catch (e) {
      setErr(e.message);
      setBusy(false);
    }
  };
  return (
    <span className="inline-flex items-center gap-1">
      <button type="button" onClick={reopen} disabled={busy}
        className="text-amber-600 hover:text-amber-700 font-medium text-xs whitespace-nowrap disabled:opacity-50"
        title="Reopen this activity: back to in progress, completion date cleared, row unlocked">
        {busy ? 'Reopening…' : 'Reopen'}
      </button>
      {err && <span className="text-[10px] text-rose-600" title={err}>!</span>}
    </span>
  );
}

// Expire an activity: retire it from the plan without performing it — a
// requirement sunset, a terminated vendor, a discontinued function. The row
// keeps who, when, and why, moves to Closed, and locks. A reason is always
// required; a monitoring-series row can also take its future months (and stop
// the series) in the same step. Reopen undoes a single row's expiry.
function ExpireButton({ act, onDone }) {
  const [open, setOpen] = React.useState(false);
  const [reason, setReason] = React.useState('');
  const [future, setFuture] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const isSeries = !!(act.monitoring_series_id && act.source_period);
  const expire = async () => {
    if (!reason.trim()) { setErr('A reason is required.'); return; }
    setBusy(true); setErr(null);
    try {
      const res = await fetch(`${API}/api/coa/activities/${act.id}/expire`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason.trim(), include_future: isSeries && future }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.message || 'Could not expire the activity.');
      setOpen(false);
      onDone && onDone();
    } catch (e) {
      setErr(e.message);
      setBusy(false);
    }
  };
  return (
    <>
      <button type="button" onClick={() => { setReason(''); setFuture(false); setErr(null); setBusy(false); setOpen(true); }}
        className="text-rose-600 hover:text-rose-700 font-medium text-xs whitespace-nowrap"
        title="Retire this activity from the plan without performing it — a reason is required and the row stays on the record">
        Expire
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={() => !busy && setOpen(false)}>
          <div className="w-[min(28rem,95vw)] rounded-2xl bg-white shadow-xl p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-medium text-slate-800">Expire this activity</h3>
            <p className="text-xs text-slate-500 mt-1">
              {[act.component, act.entity_name || 'Internal', act.source_period].filter(Boolean).join(' — ')}
            </p>
            <p className="text-sm text-slate-600 mt-3">
              The activity is retired from the plan without being performed. It moves to Closed, locks, and keeps who expired it, when, and why. Reopen undoes it.
            </p>
            <label className="block mt-4">
              <span className="text-xs font-medium text-slate-600">Reason</span>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} autoFocus
                placeholder="Why is this activity no longer required? For example: the requirement sunset, the vendor relationship ended, the function was discontinued."
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-200" />
            </label>
            {isSeries && (
              <label className="flex items-start gap-2 mt-3 cursor-pointer">
                <input type="checkbox" checked={future} onChange={(e) => setFuture(e.target.checked)} className="mt-0.5" />
                <span className="text-sm text-slate-600">
                  Also expire all future months and stop this monitoring series. Closed months are untouched.
                </span>
              </label>
            )}
            {err && <p className="text-xs text-rose-600 mt-2">{err}</p>}
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" onClick={() => setOpen(false)} disabled={busy}
                className="px-4 py-2 rounded-xl bg-slate-100 text-slate-700 text-sm font-medium border border-slate-200 hover:bg-slate-200 transition-colors disabled:opacity-50">
                Cancel
              </button>
              <button type="button" onClick={expire} disabled={busy || !reason.trim()}
                className="px-4 py-2 rounded-xl bg-rose-600 text-white text-sm font-medium border border-rose-600 hover:bg-rose-700 transition-colors disabled:opacity-50">
                {busy ? 'Expiring…' : (isSeries && future ? 'Expire this and future months' : 'Expire activity')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

// universes open the PICKER: what is actually approved in Scrubber for this
// row's month, by entity name, and compliance selects the right one. A
// universe file carries no identity of its own, so the tool never guesses —
// the submitter named it, and the selection here confirms it.
// The picker speaks in the measure's own words (operator order 2026-08-28):
// which measure this row is, and which case types of the file it scores.
const SLICE_NAMES = [
  ['IHRA', 'Initial HRA', 'Initial HRA (within 90 days of enrollment)'],
  ['AHRA', 'Annual HRA', 'Annual HRA (within 365 days of the prior HRA)'],
  ['ICP - Initial', 'Initial care plan', 'Initial care plan (within 90 days of the initial HRA or enrollment)'],
  ['ICP - Updated', 'Care plan update', 'Care plan updated after the most recent HRA'],
];
function sliceRow(measure) {
  const m = String((measure && measure.scenario_match) || '');
  return SLICE_NAMES.find(([prefix]) => m.startsWith(prefix)) || null;
}
function measureShort(measure) {
  const s = sliceRow(measure);
  return s ? s[1] : String((measure && measure.label) || 'this row').replace(/^SNPCC\s+—\s+/, '');
}
function sliceName(measure) {
  const s = sliceRow(measure);
  return s ? s[2] : String((measure && measure.scenario_match) || '');
}
function monthLong(ym) {
  const m = /^(\d{4})-(\d{2})$/.exec(String(ym || ''));
  if (!m) return ym || 'this month';
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}
/** The server judged this file's engagement date too early to close the row's month for this measure. */
function windowBlocked(sub) {
  return !!(sub && sub.window && sub.window.applies && !sub.window.ready);
}
/** This measure's cases in this month inside one submission, from the catalog's per-scenario coverage. */
function measureSlice(sub, measure, rowYm) {
  if (!Array.isArray(sub.coverage)) return null; // not an SNPE-style file: no per-scenario view
  const cov = sub.coverage.find((c) => c.month === rowYm);
  // An SNPE file with nothing dated in this month (or an empty derivative)
  // holds zero cases for this row: shown amber, never selectable.
  if (!cov || !Array.isArray(cov.scenarios)) return { cases: 0, timely: 0, untimely: 0, rate: null };
  const prefix = String((measure && measure.scenario_match) || '');
  const hits = cov.scenarios.filter((s) => !prefix || String(s.scenario).startsWith(prefix));
  const timely = hits.reduce((n, s) => n + (s.timely || 0), 0);
  const untimely = hits.reduce((n, s) => n + (s.untimely || 0), 0);
  const cases = timely + untimely;
  return { cases, timely, untimely, rate: cases > 0 ? Math.round((timely / cases) * 1000) / 10 : null };
}

function SyncButton({ act, onDone }) {
  const [busy, setBusy] = React.useState(false);
  const [note, setNote] = React.useState(null);
  const [picker, setPicker] = React.useState(null); // {period, row_entity, submissions}
  const [choice, setChoice] = React.useState(null);
  if (!act.monitoring_series_id || act.status === 'completed') return null;

  const syncWith = async (submissionId) => {
    setBusy(true); setNote(null);
    try {
      const r = await fetch(`${API}/api/coa/activities/${act.id}/sync`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submissionId ? { submission_id: submissionId } : {}),
      });
      const d = await r.json();
      if (d.success && d.selection_required) {
        const opt = await fetch(`${API}/api/coa/activities/${act.id}/scrubber-options`, { credentials: 'include' }).then((x) => x.json());
        if (opt.success) {
          setPicker(opt);
          // One file with this measure's cases for the month: pre-select it;
          // the person still confirms with the button.
          const rowYm = typeof opt.period === 'string' ? opt.period : (opt.period && opt.period.start) || '';
          const fits = (opt.submissions || []).filter((s) => { const sl = measureSlice(s, opt.measure, rowYm); return (!sl || sl.cases > 0) && !windowBlocked(s); });
          setChoice(fits.length === 1 ? fits[0].id : null);
        }
        else setNote(opt.message || 'Could not load Scrubber submissions');
      } else if (d.success) {
        setNote(d.message || 'Synced');
        setPicker(null);
        if (onDone) onDone();
      } else {
        setNote(d.message || 'Failed');
      }
    } catch (e) { setNote('Failed'); }
    setBusy(false);
  };

  // Only history syncs: a period that hasn't ended can't be scored yet.
  // (The server enforces the same rule; this label just says it politely.)
  const nowYm = new Date().toISOString().slice(0, 7);
  if (String(act.source_period || '') >= nowYm) {
    const [py, pm] = String(act.source_period).split('-').map(Number);
    const opens = py && pm
      ? new Date(Date.UTC(py, pm, 1)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
      : 'next month';
    return (
      <span className="text-[11px] text-slate-400 whitespace-nowrap"
        title="This period has not ended yet — data can be synced once the month closes.">
        Syncs {opens}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1">
      <button type="button" onClick={() => syncWith(null)} disabled={busy}
        className="text-emerald-600 hover:text-emerald-700 text-xs font-medium disabled:opacity-50"
        title={`Sync ${String(act.component || 'this row').replace(/^Case Management\s+—\s+SNPCC\s+/, '')} for ${monthLong(act.source_period)} from the Scrubber`}>
        {busy ? 'Syncing…' : 'Sync'}
      </button>
      {note && <span className="text-[10px] text-slate-500 max-w-[16rem] truncate" title={note}>{note}</span>}

      {picker && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={() => setPicker(null)}>
          <div className="w-[min(40rem,95vw)] max-h-[85vh] flex flex-col rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200/60">
              <h3 className="text-base font-bold text-slate-800">
                Sync {picker.measure && picker.measure.label ? <span className="text-orange-700">{measureShort(picker.measure)}</span> : 'this row'} — {monthLong(typeof picker.period === "string" ? picker.period : (picker.period && picker.period.start) || '')}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {picker.measure && picker.measure.scenario_match
                  ? <>This row scores only the <span className="font-semibold text-slate-700">{sliceName(picker.measure)}</span> cases of the file you pick, for this month. The same SNPE file carries Initial HRA, Annual HRA and care plan cases for many months; each row takes its own slice.</>
                  : <>This row belongs to <span className="font-semibold text-slate-700">{picker.row_entity}</span>. Pick the approved submission whose universe should score it.</>}
              </p>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
              {(picker.submissions || []).length === 0 && (
                <div className="text-sm text-slate-500 py-6 text-center">
                  Nothing approved in Scrubber matches this table for {typeof picker.period === "string" ? picker.period : (picker.period && picker.period.start) || "this month"}.
                  Upload and approve the universe in Scrubber first — name it for its entity — then sync again.
                </div>
              )}
              {(picker.submissions || []).map((sub) => {
                const on = choice === sub.id;
                const rowYm = typeof picker.period === 'string' ? picker.period : (picker.period && picker.period.start) || '';
                const declStart = sub.period && sub.period.startDate ? String(sub.period.startDate).slice(0, 10) : null;
                const declEnd = sub.period && sub.period.endDate ? String(sub.period.endDate).slice(0, 10) : null;
                // Does the DECLARED period cover the row's month?
                const periodMatches = !!(rowYm && declStart && declEnd
                  && declStart.slice(0, 7) <= rowYm && declEnd.slice(0, 7) >= rowYm);
                // This measure's own slice of the file for this month — the
                // number that will actually score the row. A file with none
                // of this measure's cases in the month cannot score it.
                const slice = measureSlice(sub, picker.measure, rowYm);
                const blocked = windowBlocked(sub);
                const usable = (!slice || slice.cases > 0) && !blocked;
                const usedFor = (picker.used_by && picker.used_by[String(sub.id)]) || [];
                return (
                  <button key={sub.id} type="button"
                    onClick={() => usable && setChoice(sub.id)}
                    disabled={!usable}
                    title={usable ? undefined : blocked ? sub.window.reason : `This file has no ${measureShort(picker.measure)} cases in ${monthLong(rowYm)}`}
                    className={`w-full text-left rounded-xl border px-4 py-3 transition ${on ? 'border-orange-400 bg-orange-50' : usable ? 'border-slate-200 hover:border-slate-300 bg-white' : 'border-slate-100 bg-slate-50 opacity-60 cursor-not-allowed'}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <span className="text-sm font-semibold text-slate-800">{sub.entity_label || 'Unnamed'}<span className="text-slate-400 font-normal"> · {sub.filename}</span></span>
                      {slice ? (
                        slice.cases > 0
                          ? <span className="text-xs font-bold tabular-nums text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 rounded-md px-2 py-0.5">{measureShort(picker.measure)} · {monthLong(rowYm)}: {slice.cases} case{slice.cases === 1 ? '' : 's'} · {slice.rate}% timely</span>
                          : <span className="text-xs font-semibold text-amber-700 bg-amber-50 ring-1 ring-amber-200 rounded-md px-2 py-0.5">No {measureShort(picker.measure)} cases in {monthLong(rowYm)}</span>
                      ) : (sub.overall && sub.overall.rate !== null && (
                        <span className="text-xs font-bold tabular-nums text-slate-600">{sub.overall.rate}% timely</span>
                      ))}
                    </div>
                    {blocked && (
                      <div className="text-[11px] text-amber-800 bg-amber-50 ring-1 ring-amber-200 rounded-md px-2 py-1 mt-1.5">
                        <span className="font-semibold">Not complete yet for {measureShort(picker.measure)}.</span> {sub.window.reason}
                      </div>
                    )}
                    {usedFor.length > 0 && (
                      <div className="text-[11px] text-slate-600 mt-1">
                        Already used this month for: <span className="font-semibold">{usedFor.map((l) => String(l).replace(/^SNPCC\s+—\s+/, '')).join(', ')}</span>
                      </div>
                    )}
                    {/* SNPE is ONE universe for the whole span, scored month by
                        month from within it — so show the months it actually
                        covers, not the upload period. Other tables keep the
                        declared-period pill unchanged. */}
                    <div className="text-xs mt-1">
                      {Array.isArray(sub.coverage) && sub.coverage.length > 0 ? (() => {
                        const inMonth = sub.coverage.find((c) => c.month === rowYm);
                        const covers = rowYm && inMonth;
                        return (
                          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md font-semibold ${covers ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' : 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'}`}>
                            One universe, scored monthly · covers {sub.coverage[0].month.replace('-', '/')} → {sub.coverage[sub.coverage.length - 1].month.replace('-', '/')}
                            {rowYm ? (covers ? ` · ${inMonth.cases} case(s) in ${rowYm.replace('-', '/')}` : ` · no cases in ${rowYm.replace('-', '/')}`) : ''}
                          </span>
                        );
                      })() : declStart || declEnd ? (
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md font-semibold ${periodMatches ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' : 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'}`}>
                          Universe period: {declStart ? declStart.replace(/-/g, '/') : '—'} → {declEnd ? declEnd.replace(/-/g, '/') : '—'}
                          {periodMatches ? ' · matches this row' : ' · different period'}
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-0.5 rounded-md font-semibold bg-slate-100 text-slate-500">No period declared at upload</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {sub.table_label || sub.table} · {sub.filename} · submitted {String(sub.submitted_at || '').slice(0, 10)}
                      {sub.cases_out_of_period > 0 && (
                        <span className="text-slate-400"> · {sub.cases_out_of_period} case(s) dated outside {rowYm || 'this month'}{sub.month_scoped ? ' (scored for this month only)' : ' (still counted)'}</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            {(() => {
              const sel = (picker.submissions || []).find((x) => x.id === choice);
              if (!sel) return null;
              const warnings = [];
              if (picker.row_entity && sel.entity_label && sel.entity_label.trim().toLowerCase() !== picker.row_entity.trim().toLowerCase()) {
                warnings.push(<span key="e">This row belongs to <b>{picker.row_entity}</b> but the selected file is named <b>{sel.entity_label}</b>.</span>);
              }
              const rowYm = typeof picker.period === 'string' ? picker.period : (picker.period && picker.period.start) || '';
              const dS = sel.period && sel.period.startDate ? String(sel.period.startDate).slice(0, 7) : null;
              const dE = sel.period && sel.period.endDate ? String(sel.period.endDate).slice(0, 7) : null;
              // A month-scored universe (SNPE) that actually carries this
              // month is the right file regardless of its declared period.
              const coversMonth = Array.isArray(sel.coverage) && sel.coverage.some((c) => c.month === rowYm);
              if (coversMonth) {
                // no declared-period warning: the slice is the score
              } else if (Array.isArray(sel.coverage) && sel.coverage.length && rowYm) {
                warnings.push(<span key="p">This universe has <b>no cases in {rowYm}</b>; it covers <b>{sel.coverage[0].month.replace('-', '/')} → {sel.coverage[sel.coverage.length - 1].month.replace('-', '/')}</b>.</span>);
              } else if (rowYm && dS && dE && (dS > rowYm || dE < rowYm)) {
                warnings.push(<span key="p">This row is for <b>{rowYm}</b> but the selected universe is declared for <b>{String(sel.period.startDate).slice(0, 10).replace(/-/g, '/')} → {String(sel.period.endDate).slice(0, 10).replace(/-/g, '/')}</b>.</span>);
              }
              return warnings.length ? (
                <div className='mx-6 mb-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 space-y-1'>
                  {warnings.map((w, i) => <div key={i}>{w}</div>)}
                  <div>Score it only if you are certain it is the right universe.</div>
                </div>
              ) : null;
            })()}
            <div className="px-6 py-4 border-t border-slate-200/60 flex items-center justify-between">
              <button type="button" onClick={() => setPicker(null)} className="text-sm text-slate-500 hover:text-slate-700">Cancel</button>
              <button type="button" disabled={!choice || busy}
                onClick={() => syncWith(choice)}
                className="px-5 py-2 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg text-sm font-semibold text-white disabled:opacity-50">
                {busy ? 'Scoring…' : 'Score this row'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </span>
  );
}

function HeaderWithTooltip({ label, tooltip, className = '', allowWrap = false }) {
  return (
    <span className={`group/tip relative inline-flex items-center gap-1.5 ${allowWrap ? 'flex-wrap' : ''} ${className}`}>
      <span className={allowWrap ? 'whitespace-normal break-words' : ''}>{label}</span>
      {tooltip && (
        <span
          className="inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500 text-[10px] font-medium transition-colors hover:bg-slate-200 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-500/50"
          aria-label={tooltip}
        >
          i
        </span>
      )}
      {tooltip && (
        <span
          role="tooltip"
          className="pointer-events-none absolute left-0 top-full z-50 mt-1 max-w-[220px] rounded-lg bg-slate-800 px-2.5 py-2 text-xs font-normal text-white shadow-lg opacity-0 transition-opacity duration-150 group-hover/tip:opacity-100"
        >
          {tooltip}
        </span>
      )}
    </span>
  );
}

export default function COAWorkPlan({ user = null, topBar = null, scope = null }) {
  const pendingOpenActivityIdRef = useRef(null);

  // scope="fdr": the SAME work plan, mounted inside the FDR app, showing and
  // creating only activities pinned to an FDR from the registry (same engine
  // pattern as the Audit app). With no scope, nothing here changes.
  const [fdrEntities, setFdrEntities] = useState([]);
  useEffect(() => {
    // Loaded in EVERY scope: the FDR window needs it to filter and to force
    // the strict select; main COA needs it so the entity field offers the
    // registered FDR names as you type — the FDR tab matches rows by exact
    // name, so a typed variant would silently fall out of FDR COA.
    fetch(`${API}/api/fdr/entities`, { credentials: 'include' })
      .then((r) => r.json()).then((d) => setFdrEntities(d.entities || [])).catch(() => {});
  }, [scope]);

  const [activeTab, setActiveTab] = useState(scope === 'fdr' ? TAB_UNIVERSE : TAB_CREATE);
  const [workPlans, setWorkPlans] = useState([]);
  const [isCoaCompliance, setIsCoaCompliance] = useState(true); // full access; false = department user (see only assigned activities)
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [activities, setActivities] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [templates, setTemplates] = useState({ activity_type: [], compliance_or_fwa: [], activity_frequency: [] });
  const [showActivityForm, setShowActivityForm] = useState(false);
  // Approved plans fold their grid behind a read-only "View approved plan"
  // (operator order 2026-08-19): Create is for authoring and approval only.
  const [showApprovedPlan, setShowApprovedPlan] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [deficiencies, setDeficiencies] = useState([]);
  const [activityAuditLogs, setActivityAuditLogs] = useState([]);
  const [activityAuditLoading, setActivityAuditLoading] = useState(false);
  const [myRebuttalQueue, setMyRebuttalQueue] = useState([]);
  const [myRebuttalLoading, setMyRebuttalLoading] = useState(false);
  const [myResultsQueue, setMyResultsQueue] = useState([]);
  const [myResultsLoading, setMyResultsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [universeActivities, setUniverseActivities] = useState([]);
  // Which board is asking. The server decides membership from the scope
  // stamped on the activity (migration 318): with scope=fdr it returns work on
  // registered FDRs, and without it, internal COA gets the organization's own
  // oversight and no FDR work. The name matching below stays as a second pair
  // of eyes on the FDR side; it never widens what the server returned.
  const scopeQ = scope === 'fdr' ? '?scope=fdr' : '';
  // FDR scope filters both lists; internal (no scope) passes through as-is.
  const fdrNameSet = scope === 'fdr' ? new Set(fdrEntities.map((e) => String(e.name).trim().toLowerCase())) : null;
  const inFdrScope = (a) => !fdrNameSet || (a.entity_name && fdrNameSet.has(String(a.entity_name).trim().toLowerCase()));
  const scopedActivities = fdrNameSet ? activities.filter(inFdrScope) : activities;
  const scopedUniverseActivities = fdrNameSet ? universeActivities.filter(inFdrScope) : universeActivities;
  // The annual plan is BUILT here in Create; Precision COA runs it after
  // approval. The wizard needs the measure catalog and the live FDR list.
  const [planWizardOpen, setPlanWizardOpen] = useState(false);
  const [planMeasures, setPlanMeasures] = useState([]);
  const [planFdrs, setPlanFdrs] = useState([]);
  useEffect(() => {
    if (!planWizardOpen) return;
    fetch(`${API}/api/coa/monitoring/measures`, { credentials: 'include' })
      .then((r) => r.json()).then((d) => d.success && setPlanMeasures(d.measures || [])).catch(() => {});
    fetch(`${API}/api/fdr/entities`, { credentials: 'include' })
      .then((r) => r.json()).then((d) => setPlanFdrs(d.entities || d.fdrs || [])).catch(() => {});
  }, [planWizardOpen]);
  const [universeLoading, setUniverseLoading] = useState(false);
  const [universeFilters, setUniverseFilters] = useState({ plan_year: '', activity_type: '', compliance_or_fwa: '', activity_frequency: '', activity_quarter: '' });
  const [approving, setApproving] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  // Read-only CMS-ordered record view (Columns A-L), openable from any row.
  const [recordFor, setRecordFor] = useState(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportModalEntered, setReportModalEntered] = useState(false);
  const [reportModalHtml, setReportModalHtml] = useState('');
  const [reportLoading, setReportLoading] = useState(false);
  // The activity id whose audit report is currently being generated — used
  // to surface the COA audit agent's progress events inside the modal.
  const [reportActivityId, setReportActivityId] = useState(null);
  const [requestingRebuttalId, setRequestingRebuttalId] = useState(null);
  const [requestingResultsId, setRequestingResultsId] = useState(null);
  // Measurements (coa_activity_metrics) for one activity, opened from the grid.
  const [measurementsFor, setMeasurementsFor] = useState(null);
  const [notificationsList, setNotificationsList] = useState([]);
  const [notificationsUnreadCount, setNotificationsUnreadCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  const handleViewReport = useCallback((activityId) => {
    setReportLoading(true);
    setReportModalEntered(false);
    setReportModalOpen(true);
    setReportModalHtml('');
    setReportActivityId(activityId);
    requestAnimationFrame(() => { requestAnimationFrame(() => setReportModalEntered(true)); });
    fetch(`${API}/api/coa/activities/${activityId}/audit-report?format=html`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { setReportLoading(false); if (d.success && d.html) setReportModalHtml(d.html); })
      .catch(() => { setReportLoading(false); });
  }, []);

  const closeReportModal = useCallback(() => {
    setReportModalEntered(false);
    setTimeout(() => { setReportModalOpen(false); setReportModalHtml(''); setReportActivityId(null); }, 200);
  }, []);

  const handleDownloadReport = useCallback((activityId, format) => {
    fetch(`${API}/api/coa/activities/${activityId}/audit-report?format=${format}`, { credentials: 'include' })
      .then((r) => { if (!r.ok) return; return r.blob(); })
      .then((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit-draft-report-${activityId.slice(0, 8)}.${format === 'docx' ? 'docx' : 'pdf'}`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => {});
  }, []);

  const loadWorkPlans = useCallback(() => {
    fetch(`${API}/api/coa/work-plans`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        if (d.success) {
          setWorkPlans(d.work_plans || []);
          setIsCoaCompliance(d.isCompliance !== false);
        }
      })
      .catch(() => setWorkPlans([]));
  }, []);

  // The FDR window auto-selects the current plan — approved first, newest
  // year wins. Nobody plans from here; they work what was approved.
  useEffect(() => {
    if (scope !== 'fdr' || selectedPlan || !workPlans.length) return;
    const sorted = [...workPlans].sort((a, b) => (b.plan_year || 0) - (a.plan_year || 0));
    setSelectedPlan(sorted.find((p) => p.status === 'approved') || sorted[0]);
  }, [scope, workPlans, selectedPlan]);

  const loadDepartments = useCallback(() => {
    fetch(`${API}/api/departments`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => Array.isArray(d) ? setDepartments(d) : setDepartments([]))
      .catch(() => setDepartments([]));
  }, []);

  const loadTemplates = useCallback(() => {
    fetch(`${API}/api/coa/templates`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => d.success && d.templates && setTemplates(d.templates))
      .catch(() => {});
  }, []);

  useEffect(() => { loadWorkPlans(); loadDepartments(); loadTemplates(); }, [loadWorkPlans, loadDepartments, loadTemplates]);

  // Deep-link from Notifications: open a specific activity when coaOpenActivityId was set in sessionStorage
  useEffect(() => {
    try {
      const id = sessionStorage.getItem('coaOpenActivityId');
      if (id) {
        sessionStorage.removeItem('coaOpenActivityId');
        pendingOpenActivityIdRef.current = id;
      }
    } catch (_) {}
  }, []);
  useEffect(() => {
    if (!pendingOpenActivityIdRef.current || workPlans.length === 0) return;
    const id = pendingOpenActivityIdRef.current;
    const actFromUniverse = universeActivities.find((a) => a.id === id);
    if (actFromUniverse) {
      const plan = workPlans.find((wp) => wp.plan_year === actFromUniverse.plan_year);
      if (plan) {
        setSelectedPlan(plan);
        setSelectedActivity(actFromUniverse);
        setShowActivityForm(false);
      }
      setActiveTab(scope === 'fdr' ? TAB_UNIVERSE : TAB_CREATE);
      pendingOpenActivityIdRef.current = null;
      return;
    }
    fetch(`${API}/api/coa/activities/${id}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        if (!d.success || !d.activity) return;
        const act = d.activity;
        const plan = workPlans.find((wp) => wp.id === act.work_plan_id);
        if (plan) {
          setSelectedPlan(plan);
          setSelectedActivity(act);
          setShowActivityForm(false);
          fetch(`${API}/api/coa/work-plans/${plan.id}/activities${scopeQ}`, { credentials: 'include' })
            .then((rr) => rr.json())
            .then((dd) => {
              if (dd.success && dd.activities) setActivities(dd.activities);
            });
        }
        setActiveTab(scope === 'fdr' ? TAB_UNIVERSE : TAB_CREATE);
        pendingOpenActivityIdRef.current = null;
      })
      .catch(() => {});
  }, [workPlans, universeActivities]);

  const loadUniverse = useCallback(() => {
    setUniverseLoading(true);
    const params = new URLSearchParams();
    params.set('completed_only', 'true'); // Precision COA: plan rows always show; legacy one-offs publish on completion
    if (universeFilters.plan_year) params.set('plan_year', universeFilters.plan_year);
    if (universeFilters.activity_type) params.set('activity_type', universeFilters.activity_type);
    if (universeFilters.compliance_or_fwa) params.set('compliance_or_fwa', universeFilters.compliance_or_fwa);
    if (universeFilters.activity_frequency) params.set('activity_frequency', universeFilters.activity_frequency);
    if (universeFilters.activity_quarter) params.set('activity_quarter', universeFilters.activity_quarter);
    if (scope === 'fdr') params.set('scope', 'fdr');
    fetch(`${API}/api/coa/universe?${params.toString()}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { if (d.success) setUniverseActivities(d.activities || []); setUniverseLoading(false); })
      .catch(() => { setUniverseActivities([]); setUniverseLoading(false); });
  }, [scope, universeFilters.plan_year, universeFilters.activity_type, universeFilters.compliance_or_fwa, universeFilters.activity_frequency, universeFilters.activity_quarter]);

  useEffect(() => {
    if (activeTab === TAB_UNIVERSE) loadUniverse();
  }, [activeTab, loadUniverse]);

  const loadMyRebuttalQueue = useCallback(() => {
    setMyRebuttalLoading(true);
    fetch(`${API}/api/coa/activities/my-rebuttal-queue`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        if (d.success) setMyRebuttalQueue(d.activities || []);
        setMyRebuttalLoading(false);
      })
      .catch(() => { setMyRebuttalQueue([]); setMyRebuttalLoading(false); });
  }, []);

  const loadMyResultsQueue = useCallback(() => {
    setMyResultsLoading(true);
    fetch(`${API}/api/coa/activities/my-results-queue`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        if (d.success) setMyResultsQueue(d.activities || []);
        setMyResultsLoading(false);
      })
      .catch(() => { setMyResultsQueue([]); setMyResultsLoading(false); });
  }, []);

  useEffect(() => {
    if (activeTab === TAB_CREATE) {
      loadMyRebuttalQueue();
      loadMyResultsQueue();
    }
  }, [activeTab, loadMyRebuttalQueue, loadMyResultsQueue]);

  const loadNotifications = useCallback(() => {
    setNotificationsLoading(true);
    fetch(`${API}/api/notifications?app=coa`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        setNotificationsLoading(false);
        if (d.success) {
          setNotificationsList(d.notifications || []);
          setNotificationsUnreadCount(d.unreadCount ?? 0);
        }
      })
      .catch(() => { setNotificationsLoading(false); });
  }, []);
  useEffect(() => {
    if (activeTab === TAB_NOTIFICATIONS) loadNotifications();
  }, [activeTab, loadNotifications]);
  useEffect(() => {
    const refreshUnread = () => {
      fetch(`${API}/api/notifications/unread-count?app=coa`, { credentials: 'include' })
        .then((r) => r.json())
        .then((d) => { if (d.success && typeof d.unreadCount === 'number') setNotificationsUnreadCount(d.unreadCount); });
    };
    refreshUnread();
    window.addEventListener('notifications-updated', refreshUnread);
    return () => window.removeEventListener('notifications-updated', refreshUnread);
  }, []);

  // Refetch on plan change AND whenever the user returns to the Create tab, so
  // drafts generated from the Universe tab (or another session) are never
  // invisible behind a stale list.
  useEffect(() => {
    if (!selectedPlan?.id) { setActivities([]); setLoading(false); return; }
    if (activeTab !== TAB_CREATE) return;
    setLoading(true);
    fetch(`${API}/api/coa/work-plans/${selectedPlan.id}/activities${scopeQ}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { if (d.success) setActivities(d.activities || []); setLoading(false); })
      .catch(() => { setActivities([]); setLoading(false); });
  }, [selectedPlan?.id, activeTab]);

  useEffect(() => {
    if (!selectedActivity?.id) { setDeficiencies([]); return; }
    fetch(`${API}/api/coa/activities/${selectedActivity.id}/deficiencies`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => d.success && setDeficiencies(d.deficiencies || []))
      .catch(() => setDeficiencies([]));
  }, [selectedActivity?.id]);

  useEffect(() => {
    if (!selectedActivity?.id) { setActivityAuditLogs([]); return; }
    setActivityAuditLoading(true);
    fetch(`${API}/api/coa/activities/${selectedActivity.id}/audit-log`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        if (d.success) setActivityAuditLogs(d.logs || []);
        else setActivityAuditLogs([]);
        setActivityAuditLoading(false);
      })
      .catch(() => { setActivityAuditLogs([]); setActivityAuditLoading(false); });
  }, [selectedActivity?.id]);

  // New plan year: carry the same activities year over year, or tick the ones
  // to carry, then add new ones (operator order 2026-08-29). The dialog owns
  // the year, the source plan and the selection; the server does the copying.
  const [rolloverOpen, setRolloverOpen] = useState(false);
  const [rolloverNote, setRolloverNote] = useState('');
  const createWorkPlan = () => setRolloverOpen(true);
  const onRolloverCreated = (d) => {
    setRolloverOpen(false);
    loadWorkPlans();
    setSelectedPlan(d.work_plan);
    setRolloverNote(d.activities_carried || d.rows_created
      ? `${d.work_plan.plan_year}: ${d.activities_carried} activities carried, ${d.rows_created} rows created${d.skipped ? `, ${d.skipped} already there` : ''}. Add new ones with Add activity or Build annual work plan.`
      : `${d.work_plan.plan_year} plan ready. Add activities with Add activity or Build annual work plan.`);
  };

  const approveWorkPlan = () => {
    if (!selectedPlan || !user?.id) return;
    setApproving(true);
    setError('');
    fetch(`${API}/api/coa/work-plans/${selectedPlan.id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'approved', approved_by: user.id })
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.success) {
          if (d.work_plan) setSelectedPlan(d.work_plan);
          loadWorkPlans();
        } else setError(d.message || 'Approval failed');
        setApproving(false);
      })
      .catch((e) => { setError(e.message); setApproving(false); });
  };

  const handleViewFromUniverse = (activity) => {
    const plan = workPlans.find((wp) => wp.plan_year === activity.plan_year);
    if (plan) {
      setSelectedPlan(plan);
      setSelectedActivity(activity);
      setShowActivityForm(false);
    }
    setActiveTab(TAB_CREATE);
  };

  const handleRequestRebuttalFromCreateTab = useCallback((activityId) => {
    setRequestingRebuttalId(activityId);
    fetch(`${API}/api/coa/activities/${activityId}/request-rebuttal`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    })
      .then((r) => r.json())
      .then((d) => {
        setRequestingRebuttalId(null);
        if (d.success && selectedPlan?.id) {
          fetch(`${API}/api/coa/work-plans/${selectedPlan.id}/activities${scopeQ}`, { credentials: 'include' })
            .then((r) => r.json())
            .then((dd) => {
              if (dd.success && dd.activities) {
                setActivities(dd.activities);
                const updated = dd.activities.find((a) => a.id === selectedActivity?.id);
                if (updated) setSelectedActivity(updated);
              }
            });
        }
      })
      .catch(() => setRequestingRebuttalId(null));
  }, [selectedPlan?.id, selectedActivity?.id]);

  const handleRequestResultsFromCreateTab = useCallback((activityId) => {
    setRequestingResultsId(activityId);
    fetch(`${API}/api/coa/activities/${activityId}/request-results`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    })
      .then((r) => r.json())
      .then((d) => {
        setRequestingResultsId(null);
        if (d.success && selectedPlan?.id) {
          fetch(`${API}/api/coa/work-plans/${selectedPlan.id}/activities${scopeQ}`, { credentials: 'include' })
            .then((r) => r.json())
            .then((dd) => {
              if (dd.success && dd.activities) {
                setActivities(dd.activities);
                const updated = dd.activities.find((a) => a.id === selectedActivity?.id);
                if (updated) setSelectedActivity(updated);
              }
            });
          loadMyResultsQueue();
        }
      })
      .catch(() => setRequestingResultsId(null));
  }, [selectedPlan?.id, selectedActivity?.id, loadMyResultsQueue]);

  return (
    // Under the FDR window the work plan is NOT a nested scroll pane: the FDR
    // page scrolls as one document, its header scrolls away, and every month
    // row sits in normal flow (operator report 2026-10-03: months hidden or
    // hard to see in FDR COA, unlike the main COA). Standalone COA keeps its
    // own full-height pane exactly as before.
    <div className={scope === 'fdr' ? 'flex bg-slate-50' : 'h-full flex overflow-hidden bg-slate-50'}>
      {/* Left menu — gradient, icon + label, active left bar. Hidden under
          the FDR window (operator order 2026-08-21): FDR COA is a HEADLESS
          view of Precision COA only — one nav layer, no second chrome. */}
      {scope !== 'fdr' && (
      <aside className="w-24 bg-gradient-to-b from-slate-900/95 via-slate-800/90 to-slate-900/95 backdrop-blur-xl border-r border-slate-700/50 flex flex-col flex-shrink-0 items-center py-6 space-y-4 shadow-2xl">
        <nav className="flex flex-col items-center space-y-3 flex-1" aria-label="Compliance Audit and Monitoring Work Plan navigation">
          <div className="relative group">
            {activeTab === TAB_CREATE && (
              <div className="absolute -left-3 w-1 h-8 bg-gradient-to-b from-orange-400 to-orange-500 rounded-r-full shadow-lg animate-pulse" />
            )}
            <button
              type="button"
              onClick={() => setActiveTab(TAB_CREATE)}
              className={`
                relative w-14 h-14 rounded-2xl flex flex-col items-center justify-center transition-all duration-300 ease-out
                ${activeTab === TAB_CREATE
                  ? 'bg-gradient-to-br from-orange-500/20 to-orange-600/20 border border-orange-400/30 shadow-lg shadow-orange-500/25 scale-110'
                  : 'bg-slate-800/50 hover:bg-slate-700/70 border border-slate-600/30 hover:border-slate-500/50 hover:scale-105'
                }
              `}
              aria-label="Create"
            >
              <svg className={`w-6 h-6 transition-all duration-300 ${activeTab === TAB_CREATE ? 'text-orange-300 scale-110' : 'text-slate-300 group-hover:text-white'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className={`text-xs font-medium mt-1 transition-all duration-300 ${activeTab === TAB_CREATE ? 'text-orange-300' : 'text-slate-400 group-hover:text-slate-200'}`}>
                Create
              </span>
            </button>
          </div>
          <div className="relative group">
            {planWizardOpen && (
            <SetupWizard
              measures={planMeasures}
              fdrs={planFdrs}
              departments={departments}
              onClose={() => setPlanWizardOpen(false)}
              onSaved={(d) => {
                setPlanWizardOpen(false);
                loadWorkPlans && loadWorkPlans();
                alert(d && d.rows_created !== undefined
                  ? `Plan saved — ${d.rows_created} row(s) created for the year. They are in Precision COA.`
                  : 'Plan saved.');
              }}
            />
          )}

          {activeTab === TAB_UNIVERSE && (
              <div className="absolute -left-3 w-1 h-8 bg-gradient-to-b from-orange-400 to-orange-500 rounded-r-full shadow-lg animate-pulse" />
            )}
            <button
              type="button"
              onClick={() => setActiveTab(TAB_UNIVERSE)}
              className={`
                relative w-14 h-14 rounded-2xl flex flex-col items-center justify-center transition-all duration-300 ease-out
                ${activeTab === TAB_UNIVERSE
                  ? 'bg-gradient-to-br from-orange-500/20 to-orange-600/20 border border-orange-400/30 shadow-lg shadow-orange-500/25 scale-110'
                  : 'bg-slate-800/50 hover:bg-slate-700/70 border border-slate-600/30 hover:border-slate-500/50 hover:scale-105'
                }
              `}
              aria-label="Precision COA"
            >
              <svg className={`w-6 h-6 transition-all duration-300 ${activeTab === TAB_UNIVERSE ? 'text-orange-300 scale-110' : 'text-slate-300 group-hover:text-white'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              <span className={`text-xs font-medium mt-1 transition-all duration-300 ${activeTab === TAB_UNIVERSE ? 'text-orange-300' : 'text-slate-400 group-hover:text-slate-200'}`}>
                Precision COA
              </span>
            </button>
          </div>
          <div className="relative group">
            {activeTab === TAB_MONITORING && (
              <div className="absolute -left-3 w-1 h-8 bg-gradient-to-b from-orange-400 to-orange-500 rounded-r-full shadow-lg animate-pulse" />
            )}
            <button
              type="button"
              onClick={() => setActiveTab(TAB_MONITORING)}
              className={`
                relative w-14 h-14 rounded-2xl flex flex-col items-center justify-center transition-all duration-300 ease-out
                ${activeTab === TAB_MONITORING
                  ? 'bg-gradient-to-br from-orange-500/20 to-orange-600/20 border border-orange-400/30 shadow-lg shadow-orange-500/25 scale-110'
                  : 'bg-slate-800/50 hover:bg-slate-700/70 border border-slate-600/30 hover:border-slate-500/50 hover:scale-105'
                }
              `}
              aria-label="Charting"
            >
              <svg className={`w-6 h-6 transition-all duration-300 ${activeTab === TAB_MONITORING ? 'text-orange-300 scale-110' : 'text-slate-300 group-hover:text-white'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
              </svg>
              <span className={`text-xs font-medium mt-1 transition-all duration-300 ${activeTab === TAB_MONITORING ? 'text-orange-300' : 'text-slate-400 group-hover:text-slate-200'}`}>
                Charting
              </span>
            </button>
          </div>
          <div className="relative group">
            {activeTab === TAB_NOTIFICATIONS && (
              <div className="absolute -left-3 w-1 h-8 bg-gradient-to-b from-orange-400 to-orange-500 rounded-r-full shadow-lg animate-pulse" />
            )}
            <button
              type="button"
              onClick={() => setActiveTab(TAB_NOTIFICATIONS)}
              className={`
                relative w-14 h-14 rounded-2xl flex flex-col items-center justify-center transition-all duration-300 ease-out
                ${activeTab === TAB_NOTIFICATIONS
                  ? 'bg-gradient-to-br from-orange-500/20 to-orange-600/20 border border-orange-400/30 shadow-lg shadow-orange-500/25 scale-110'
                  : 'bg-slate-800/50 hover:bg-slate-700/70 border border-slate-600/30 hover:border-slate-500/50 hover:scale-105'
                }
              `}
              aria-label="Notifications"
            >
              <span className="relative inline-block">
                <svg className={`w-6 h-6 transition-all duration-300 ${activeTab === TAB_NOTIFICATIONS ? 'text-orange-300 scale-110' : 'text-slate-300 group-hover:text-white'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {notificationsUnreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-1 min-w-[16px] h-4 px-1 flex items-center justify-center text-[10px] font-bold bg-orange-500 text-white rounded-full">
                    {notificationsUnreadCount > 99 ? '99+' : notificationsUnreadCount}
                  </span>
                )}
              </span>
              <span className={`text-xs font-medium mt-1 transition-all duration-300 ${activeTab === TAB_NOTIFICATIONS ? 'text-orange-300' : 'text-slate-400 group-hover:text-slate-200'}`}>
                Notifications
              </span>
            </button>
          </div>
        </nav>
        <div className="px-2 pt-4 border-t border-slate-700/50">
          <p className="text-[10px] text-slate-500 text-center leading-tight tracking-wide" title="CMS CPE Protocol">
            Compliance Audit &amp; Monitoring
          </p>
        </div>
      </aside>
      )}

      <div className={scope === 'fdr' ? 'flex-1 flex flex-col min-w-0' : 'flex-1 flex flex-col overflow-hidden'}>
        {/* Header — Privacy Command Center style. Hidden under the FDR
            window: the FDR app already carries its own header. */}
        {scope !== 'fdr' && (
        <header className="bg-white border-b border-slate-200/60 px-8 py-6 flex-shrink-0 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-orange-500/25 ring-2 ring-white/20">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800 tracking-tight">Compliance Audit and Monitoring Work Plan</h1>
              <p className="text-slate-500 text-sm mt-0.5">Compliance Oversight Activities · CMS CPE Protocol</p>
            </div>
          </div>
        </header>
        )}

        {topBar}

        <main className={scope === 'fdr' ? 'flex-1 min-w-0' : 'flex-1 overflow-auto'}>
          {/* Full-width: the work plan is a wide CMS grid; capping it at
              max-w-6xl forced ~3,850px of columns into ~1,152px and pushed the
              horizontal scrollbar below the fold. */}
          <div className="w-full px-6 py-6">
            {error && (
              <div className="mb-4 p-3 rounded-xl bg-red-50 text-red-700 ring-1 ring-red-200 text-sm">{error}</div>
            )}

            {activeTab === TAB_CREATE && (
              <div className="w-full">
                {/* My results queue — assignees see Monitoring/SIU activities where results were requested */}
                {myResultsQueue.length > 0 && (
                  <div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200">
                    <h3 className="font-medium text-amber-900 mb-2">Results requested — enter your activity results</h3>
                    <p className="text-sm text-amber-800 mb-3">Compliance has requested you enter results for these Monitoring/SIU activities. Click to open and add metric, threshold, or remediation activities.</p>
                    <ul className="space-y-2">
                      {myResultsQueue.map((act) => (
                        <li key={act.id}>
                          <button
                            type="button"
                            onClick={() => {
                              const plan = workPlans.find((wp) => wp.id === act.work_plan_id);
                              if (plan) {
                                setSelectedPlan(plan);
                                fetch(`${API}/api/coa/work-plans/${plan.id}/activities${scopeQ}`, { credentials: 'include' })
                                  .then((r) => r.json())
                                  .then((d) => {
                                    if (d.success && d.activities) {
                                      setActivities(d.activities);
                                      const a = d.activities.find((x) => x.id === act.id);
                                      if (a) setSelectedActivity(a);
                                    }
                                  });
                              }
                            }}
                            className="text-left w-full px-4 py-2.5 rounded-xl bg-white border border-amber-200 hover:bg-amber-50 text-sm font-medium text-slate-800 transition-colors"
                          >
                            {act.component} {act.entity_name && `— ${act.entity_name}`} · {act.activity_type} · {act.results_requested_at ? `Requested ${isoToCmsDate(act.results_requested_at)}` : ''}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Work plan row — same pattern as Privacy: one row with selector + action */}
                <div className="flex flex-wrap items-center gap-4 mb-6">
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-slate-700">Work plan</label>
                    <select
                      value={selectedPlan?.id || ''}
                      onChange={(e) => {
                        const id = e.target.value;
                        const wp = workPlans.find((p) => p.id === id);
                        setSelectedPlan(wp || null);
                        setShowActivityForm(false);
                        setSelectedActivity(null);
                      }}
                      className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 min-w-[160px]"
                    >
                      <option value="">— Select year —</option>
                      {workPlans.map((wp) => (
                        <option key={wp.id} value={wp.id}>{wp.plan_year} — {wp.status}</option>
                      ))}
                    </select>
                  </div>
                  {isCoaCompliance && (
                    <>
                      <button
                        type="button"
                        onClick={() => setPlanWizardOpen(true)}
                        className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-white font-medium shadow-sm shadow-orange-500/25 hover:opacity-90 transition-all"
                        title="Define the year's activities — months, benchmarks, internal or per-FDR — and every row is created for the year"
                      >
                        Build annual work plan
                      </button>
                      <button
                        type="button"
                        onClick={createWorkPlan}
                        className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white font-medium shadow-sm shadow-orange-500/25 hover:opacity-90 transition-all"
                        title="Open a new plan year: carry every activity forward, or only the ones you tick, then add new ones"
                      >
                        + New work plan
                      </button>
                    </>
                  )}
                  {rolloverOpen && (
                    <RolloverDialog api={API} scopeQ={scopeQ} workPlans={workPlans}
                      onClose={() => setRolloverOpen(false)} onCreated={onRolloverCreated} />
                  )}
                  {rolloverNote && (
                    <span className="text-sm text-emerald-700">{rolloverNote}</span>
                  )}
                  {isCoaCompliance && selectedPlan && (
                    <a
                      href={`${API}/api/coa/work-plans/${selectedPlan.id}/export`}
                      className="px-5 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 font-medium shadow-sm hover:border-slate-300 transition-all"
                    >
                      Export Excel
                    </a>
                  )}
                </div>

                {!selectedPlan && (
                  <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-8 text-center text-sm text-slate-500">
                    Select a work plan above or create one to add activities.
                  </div>
                )}
                {selectedPlan && (
                  <>
                    {/* Approval audit trail and Approve action */}
                    <div className="flex flex-wrap items-center gap-4 mb-4 p-4 rounded-xl bg-slate-50/50 border border-slate-200/60">
                      {selectedPlan.approved_at ? (
                        <p className="text-slate-700 text-sm">
                          <span className="font-medium">Approved</span> on {isoToCmsDate(selectedPlan.approved_at)}
                          {selectedPlan.approved_by_name && (
                            <> by <span className="font-medium">{selectedPlan.approved_by_name}</span></>
                          )}
                        </p>
                      ) : (
                        <p className="text-slate-600 text-sm">Plan not yet approved.</p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {selectedPlan.status === 'draft' && user?.id && isCoaCompliance && (
                          <button
                            type="button"
                            onClick={approveWorkPlan}
                            disabled={approving}
                            className="px-4 py-2 rounded-xl bg-emerald-600 text-white font-medium text-sm hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
                          >
                            {approving ? 'Approving…' : 'Approve plan (CEO)'}
                          </button>
                        )}
                        {isCoaCompliance && selectedPlan.id && (
                          <button
                            type="button"
                            onClick={() => {
                              // Deep-link into the Approvals app with this work plan
                              // pre-seeded into the wizard. The Approvals app reads
                              // seedType + seedId from the URL on mount.
                              const params = new URLSearchParams({
                                app: 'ratify',
                                seedType: 'coa_work_plan',
                                seedId: selectedPlan.id,
                              });
                              window.location.assign(`/app?${params.toString()}`);
                            }}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 text-white font-medium text-sm hover:bg-indigo-700 shadow-sm transition-all"
                            title="Send this work plan to SignoffHub for secure, audit-grade sign-off by your Compliance Committee or Board."
                          >
                            Send to SignoffHub
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Activities section — same layout as Privacy "Active incidents".
                        Once the plan is APPROVED, this tab is the plan RECORD
                        (operator order 2026-08-19): the grid folds behind
                        "View approved plan" read-only, day-to-day work moves
                        to Precision COA, and any addition after approval is
                        an explicit, counted plan amendment. */}
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h2 className="text-lg font-bold text-slate-800 tracking-tight">Activities — {selectedPlan.plan_year}</h2>
                        <p className="text-slate-500 text-sm mt-0.5">
                          {selectedPlan.status === 'approved'
                            ? 'The approved plan of record. Day-to-day work happens in Precision COA; additions here are plan amendments.'
                            : 'Add activities; track deficiencies and rebuttals. One row per activity.'}
                        </p>
                      </div>
                      {isCoaCompliance && (
                        <button
                          type="button"
                          onClick={() => { setShowActivityForm(true); setSelectedActivity(null); setEditingActivity(null); }}
                          className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white font-medium shadow-sm shadow-orange-500/25 hover:opacity-90 transition-all"
                          title={selectedPlan.status === 'approved' ? 'The plan is approved — this addition is recorded as a plan amendment.' : undefined}
                        >
                          {selectedPlan.status === 'approved' ? '+ Add activity (amendment)' : '+ Add activity'}
                        </button>
                      )}
                    </div>

                    {showActivityForm && (
                      <ActivityForm
                        workPlanId={selectedPlan.id}
                        departments={departments}
                        fdrEntities={scope === 'fdr' ? fdrEntities : null}
                        fdrOptions={fdrEntities}
                        templates={templates}
                        onSave={() => {
                          setShowActivityForm(false);
                          fetch(`${API}/api/coa/work-plans/${selectedPlan.id}/activities${scopeQ}`, { credentials: 'include' })
                            .then((r) => r.json())
                            .then((d) => d.success && setActivities(d.activities || []));
                        }}
                        onCancel={() => setShowActivityForm(false)}
                      />
                    )}

                    {loading && <div className="text-slate-500 py-8">Loading activities...</div>}
                    {!loading && activities.length === 0 && !showActivityForm && (
                      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-8 text-center text-sm text-slate-500">
                        No activities yet. Click &quot;Add activity&quot; to add an oversight activity.
                      </div>
                    )}
                    {!loading && activities.length > 0 && selectedPlan.status === 'approved' && !showApprovedPlan && (
                      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6 mb-6">
                        {(() => {
                          const byType = {};
                          const components = new Set();
                          let amended = 0;
                          const approvedAt = selectedPlan.approved_at ? String(selectedPlan.approved_at) : null;
                          for (const a of activities) {
                            byType[a.activity_type] = (byType[a.activity_type] || 0) + 1;
                            if (a.component) components.add([a.component, a.entity_name || ''].join('::'));
                            // An amendment is a HUMAN addition after approval.
                            // The monitoring engine and the oversight agent
                            // generate period rows all year by design — those
                            // are the approved plan unfolding, not changes.
                            const manual = !a.monitoring_series_id && !a.generated_by_agent && !a.source_app;
                            if (manual && approvedAt && a.created_at && String(a.created_at) > approvedAt) amended += 1;
                          }
                          return (
                            <>
                              <div className="flex flex-wrap gap-6 text-sm text-slate-700">
                                <span><span className="font-bold text-slate-900">{components.size}</span> measures</span>
                                <span><span className="font-bold text-slate-900">{activities.length}</span> planned activities</span>
                                {Object.entries(byType).map(([t, n]) => (
                                  <span key={t}><span className="font-bold text-slate-900">{n}</span> {t}</span>
                                ))}
                                {amended > 0 && (
                                  <span className="text-amber-700 font-semibold" title="Activities added after the CEO approved the plan — each one is on the audit trail.">
                                    {amended} added after approval
                                  </span>
                                )}
                              </div>
                              <button type="button" onClick={() => setShowApprovedPlan(true)}
                                className="mt-4 px-4 py-2 rounded-xl bg-slate-100 text-slate-700 text-sm font-medium border border-slate-200 hover:bg-slate-200 transition-colors">
                                View approved plan
                              </button>
                            </>
                          );
                        })()}
                      </div>
                    )}
                    {!loading && activities.length > 0 && (selectedPlan.status !== 'approved' || showApprovedPlan) && (
                      <>
                        {selectedPlan.status === 'approved' && (
                          <button type="button" onClick={() => setShowApprovedPlan(false)}
                            className="mb-3 text-xs font-semibold text-slate-500 hover:text-slate-700 underline">
                            Hide the plan grid
                          </button>
                        )}
                        <WorkActivitiesTable
                          activities={scopedActivities}
                          fdrEntities={scope === 'fdr' ? fdrEntities : null}
                          selectedActivity={selectedActivity}
                          setSelectedActivity={setSelectedActivity}
                          setEditingActivity={(act) => { setShowActivityForm(false); setEditingActivity(act); }}
                          onRefresh={() => {
                            fetch(`${API}/api/coa/work-plans/${selectedPlan.id}/activities${scopeQ}`, { credentials: 'include' })
                              .then((r) => r.json())
                              .then((d) => d.success && setActivities(d.activities || []));
                          }}
                          templates={templates}
                          departments={departments}
                          isCoaCompliance={isCoaCompliance && selectedPlan.status !== 'approved'}
                          onViewReport={handleViewReport}
                          onDownloadReport={handleDownloadReport}
                          onViewMeasurements={setMeasurementsFor}
                          onViewRecord={setRecordFor}
                        />
                        {editingActivity && (
                          <ActivityEditForm
                            activity={editingActivity}
                            departments={departments}
                            fdrEntities={scope === 'fdr' ? fdrEntities : null}
                            fdrOptions={fdrEntities}
                            templates={templates}
                            onSave={() => {
                              fetch(`${API}/api/coa/work-plans/${selectedPlan.id}/activities${scopeQ}`, { credentials: 'include' })
                                .then((r) => r.json())
                                .then((d) => {
                                  if (d.success && d.activities) {
                                    setActivities(d.activities);
                                    const updated = d.activities.find((a) => a.id === editingActivity.id);
                                    if (updated) setSelectedActivity(updated);
                                  }
                                  setEditingActivity(null);
                                });
                            }}
                            onCancel={() => setEditingActivity(null)}
                          />
                        )}
                      </>
                    )}

                    {selectedActivity && (selectedActivity.activity_type === 'Monitoring' || selectedActivity.activity_type === 'Investigations') && (
                      <ResultsEntryPanel
                        activity={selectedActivity}
                        user={user}
                        isCoaCompliance={isCoaCompliance}
                        onRequestResults={handleRequestResultsFromCreateTab}
                        requestingResultsId={requestingResultsId}
                        onRefresh={() => {
                          fetch(`${API}/api/coa/work-plans/${selectedPlan.id}/activities${scopeQ}`, { credentials: 'include' })
                            .then((r) => r.json())
                            .then((d) => {
                              if (d.success && d.activities) {
                                setActivities(d.activities);
                                const updated = d.activities.find((a) => a.id === selectedActivity.id);
                                if (updated) setSelectedActivity(updated);
                              }
                            });
                          loadMyResultsQueue();
                        }}
                      />
                    )}
                    {selectedActivity && (
                      <DeficienciesPanel
                        activity={selectedActivity}
                        deficiencies={deficiencies}
                        isCoaCompliance={isCoaCompliance}
                        onViewReport={handleViewReport}
                        onDownloadReport={handleDownloadReport}
                        onRequestRebuttal={handleRequestRebuttalFromCreateTab}
                        requestingId={requestingRebuttalId}
                        onRefresh={() => {
                          fetch(`${API}/api/coa/activities/${selectedActivity.id}/deficiencies`, { credentials: 'include' })
                            .then((r) => r.json())
                            .then((d) => d.success && setDeficiencies(d.deficiencies || []));
                        }}
                        onCapCreated={() => {
                          fetch(`${API}/api/coa/work-plans/${selectedPlan.id}/activities${scopeQ}`, { credentials: 'include' })
                            .then((r) => r.json())
                            .then((d) => {
                              if (d.success && d.activities) {
                                setActivities(d.activities);
                                const updated = d.activities.find((a) => a.id === selectedActivity.id);
                                if (updated) setSelectedActivity(updated);
                              }
                            });
                        }}
                        onClose={() => setSelectedActivity(null)}
                      />
                    )}

                    {selectedActivity && (
                      <AuditTrailPanel
                        activity={selectedActivity}
                        loading={activityAuditLoading}
                        logs={activityAuditLogs}
                      />
                    )}
                  </>
                )}
              </div>
            )}

          {measurementsFor && (
            <MeasurementsModal
              activityId={measurementsFor.id}
              activityName={measurementsFor.name}
              canEditThreshold={isCoaCompliance}
              onClose={() => setMeasurementsFor(null)}
            />
          )}

          {recordFor && (
            <COARecordModal activity={recordFor} onClose={() => setRecordFor(null)} />
          )}

          {reportModalOpen && createPortal(
            <div
              className={`fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm transition-opacity duration-200 ease-out ${reportModalEntered ? 'opacity-100' : 'opacity-0'}`}
              role="dialog"
              aria-modal="true"
            >
              <div
                className={`bg-white rounded-2xl shadow-2xl border border-slate-200/60 max-w-6xl w-[min(72rem,95vw)] max-h-[90vh] flex flex-col transition-all duration-200 ease-out ${reportModalEntered ? 'opacity-100 scale-100' : 'opacity-0 scale-[0.98]'}`}
              >
                <div className="flex justify-between items-center p-5 border-b border-slate-200/60">
                  <h3 className="font-semibold text-slate-800">Audit Draft Report</h3>
                  <button type="button" onClick={closeReportModal} className="px-3 py-1.5 rounded-xl bg-slate-100 text-slate-700 text-sm font-medium border border-slate-200 hover:bg-slate-200 transition-colors">Close</button>
                </div>
                <div className="flex-1 overflow-auto p-4 min-h-[400px]">
                  {reportLoading && <p className="text-slate-500">Loading report…</p>}
                  {/* Agent activity for the in-flight audit report generator.
                      Renders nothing until the COA audit agent emits events. */}
                  {reportActivityId && <COAAuditAgentActivity activityId={reportActivityId} />}
                  {!reportLoading && reportModalHtml && <iframe title="Audit report" srcDoc={reportModalHtml} className="w-full min-h-[70vh] border border-slate-200/60 rounded-xl" />}
                  {!reportLoading && !reportModalHtml && <p className="text-slate-500">Could not load report.</p>}
                </div>
              </div>
            </div>,
            document.body
          )}

          {activeTab === TAB_UNIVERSE && (
            <UniverseView
              activities={scopedUniverseActivities}
              fdrEntities={scope === 'fdr' ? fdrEntities : null}
              fdrOptions={fdrEntities}
              loading={universeLoading}
              filters={universeFilters}
              onFiltersChange={setUniverseFilters}
              onRefresh={loadUniverse}
              onViewActivity={handleViewFromUniverse}
              workPlans={workPlans}
              templates={templates}
              isCoaCompliance={isCoaCompliance}
              onViewReport={handleViewReport}
              onDownloadReport={handleDownloadReport}
              onViewMeasurements={setMeasurementsFor}
              onViewRecord={setRecordFor}
              departments={departments}
            />
          )}

          {activeTab === TAB_MONITORING && (
            <ChartingBoard scope={scope} orgName={user?.organization_name || ''} />
          )}

          {activeTab === TAB_NOTIFICATIONS && (
            <div className="max-w-4xl">
              <h2 className="text-xl font-bold text-slate-800 mb-1">Notifications</h2>
              <p className="text-slate-500 text-sm mb-6">
                Rebuttal requests and responses for this work plan.
              </p>
              {notificationsLoading ? (
                <p className="text-slate-500 py-8">Loading notifications…</p>
              ) : notificationsList.length === 0 ? (
                <div className="rounded-2xl border border-slate-200/60 bg-white shadow-sm p-8 text-center text-sm text-slate-500">
                  No notifications yet.
                </div>
              ) : (
                <ul className="space-y-3">
                  {notificationsList.map((n) => {
                    const formatDate = (d) => {
                      if (!d) return '';
                      const date = new Date(d);
                      const now = new Date();
                      return date.toDateString() === now.toDateString() ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : date.toLocaleDateString();
                    };
                    const handleViewNotification = () => {
                      if (n.link_app === 'coa' && n.link_id) {
                        pendingOpenActivityIdRef.current = n.link_id;
                        setActiveTab(TAB_UNIVERSE);
                      }
                      if (!n.read_at) {
                        fetch(`${API}/api/notifications/${n.id}/read`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' } })
                          .then((r) => r.json())
                          .then((d) => {
                            if (d.success) {
                              setNotificationsList((prev) => prev.map((x) => (x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x)));
                              setNotificationsUnreadCount((c) => Math.max(0, c - 1));
                              window.dispatchEvent(new CustomEvent('notifications-updated'));
                            }
                          });
                      }
                    };
                    return (
                      <li
                        key={n.id}
                        className={`rounded-2xl border p-4 transition-colors shadow-sm ${n.read_at ? 'bg-white border-slate-200/60' : 'bg-amber-50 border-amber-200'}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-slate-900">{n.title}</p>
                            {n.body && <p className="text-sm text-slate-600 mt-1">{n.body}</p>}
                            <p className="text-xs text-slate-400 mt-2">{formatDate(n.created_at)}</p>
                          </div>
                          {n.link_app === 'coa' && n.link_id && (
                            <button
                              type="button"
                              onClick={handleViewNotification}
                              className="flex-shrink-0 px-3 py-1.5 text-sm font-medium text-orange-600 hover:text-orange-800 hover:bg-orange-50 rounded-xl transition-colors"
                            >
                              View
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
        </main>
      </div>
    </div>
  );
}

const ALL_UNIVERSE_COLUMNS = [...COA_COLUMNS, ...EXTRA_UNIVERSE_COLUMNS];

function UniverseView({ activities, fdrEntities = null, fdrOptions = [], loading, filters, onFiltersChange, onRefresh, onViewActivity, workPlans, templates, isCoaCompliance = true, onViewReport, onDownloadReport, onViewMeasurements, onViewRecord, departments = [] }) {
  // Edit happens HERE, in a modal — never a jump to the Create tab.
  const [editRow, setEditRow] = React.useState(null);
  const [requestingRebuttalId, setRequestingRebuttalId] = useState(null);
  const [requestingResultsId, setRequestingResultsId] = useState(null);
  const [runningAgent, setRunningAgent] = useState(false);
  const [agentMsg, setAgentMsg] = useState('');
  const [agentDetails, setAgentDetails] = useState([]);
  // Reporting month for the oversight agent. Defaults to the prior calendar
  // month, but the user can point it at any month (universe files often cover
  // earlier months than the one you happen to click the button in).
  const [agentPeriod, setAgentPeriod] = useState(() => {
    const d = new Date();
    d.setUTCDate(1);
    d.setUTCMonth(d.getUTCMonth() - 1);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  });
  const currentYear = new Date().getFullYear();
  const years = workPlans.length ? [...new Set(workPlans.map((wp) => wp.plan_year))].sort((a, b) => b - a) : [currentYear];

  // Run the monthly oversight agent for the chosen month. Drafts one Monitoring
  // (FWA for Sentinel) activity per source app, then refreshes the universe.
  // Every app's outcome is shown, including WHY one produced nothing, so an
  // empty result is never a mystery.
  const AGENT_APP_LABELS = {
    privacy: 'Privacy Incidents',
    memo: 'Mandates and Memos',
    cap: 'Corrective Action Plans',
    cil: 'Compliance Issues Log',
    sentinel: 'FWA Investigations',
    scrubber: 'Universe Timeliness (Scrubber)',
    monitoring: 'Monitoring Series',
  };
  const handleRunAgent = () => {
    if (runningAgent) return;
    setRunningAgent(true);
    setAgentMsg('');
    setAgentDetails([]);
    fetch(`${API}/api/coa/agent/run-month`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ period: agentPeriod }),
    })
      .then((r) => r.json())
      .then((d) => {
        if (!d.success) throw new Error(d.message || 'Run failed');
        const res = d.results || {};
        const ok = Object.values(res).filter((x) => x.status === 'ok').length;
        setAgentMsg(ok > 0
          ? `Drafted ${ok} monitoring ${ok === 1 ? 'activity' : 'activities'} for ${d.period}. Open the Create tab, review the "Agent draft" ${ok === 1 ? 'entry' : 'entries'}, and mark ${ok === 1 ? 'it' : 'them'} completed to publish into this Universe view.`
          : `No activities drafted for ${d.period}. Here is what each source reported:`);
        setAgentDetails(Object.entries(res).map(([app, r]) => {
          const label = AGENT_APP_LABELS[app] || app;
          if (r.status === 'ok') {
            const defs = Number.isInteger(r.deficiencies) ? `, ${r.deficiencies} deficienc${r.deficiencies === 1 ? 'y' : 'ies'}` : '';
            const n = r.activities || 1;
            const what = n > 1
              ? `${n} activities drafted (${(r.components || []).join(', ')})`
              : `Activity drafted${r.components && r.components.length ? ` for ${r.components[0]}` : ''}`;
            return { app: label, tone: 'ok', text: `${what}${defs}.` };
          }
          if (r.status === 'skipped') return { app: label, tone: 'skip', text: r.reason || 'Nothing to report this month.' };
          return { app: label, tone: 'error', text: r.error || 'Failed.' };
        }));
        onRefresh();
      })
      .catch((e) => setAgentMsg(e.message || 'Run failed'))
      .finally(() => setRunningAgent(false));
  };

  const handleRequestRebuttal = (activityId) => {
    setRequestingRebuttalId(activityId);
    fetch(`${API}/api/coa/activities/${activityId}/request-rebuttal`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    })
      .then((r) => r.json())
      .then((d) => {
        setRequestingRebuttalId(null);
        if (d.success) onRefresh();
      })
      .catch(() => setRequestingRebuttalId(null));
  };

  const handleRequestResults = (activityId) => {
    setRequestingResultsId(activityId);
    fetch(`${API}/api/coa/activities/${activityId}/request-results`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    })
      .then((r) => r.json())
      .then((d) => {
        setRequestingResultsId(null);
        if (d.success) onRefresh();
      })
      .catch(() => setRequestingResultsId(null));
  };

  // Bulk request (operator order 2026-08-19): tick any number of month rows
  // and request results on all of them in one click. Each assignee gets one
  // notification per activity; rows that do not take result requests
  // (Auditing) are counted as skipped, never silently dropped.
  const [bulkSelected, setBulkSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkNote, setBulkNote] = useState('');
  const toggleBulk = (id) => setBulkSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selectManyBulk = (ids, on) => setBulkSelected((prev) => {
    const next = new Set(prev);
    for (const id of ids) { if (on) next.add(id); else next.delete(id); }
    return next;
  });
  const requestResultsBulk = async () => {
    if (bulkBusy || bulkSelected.size === 0) return;
    setBulkBusy(true); setBulkNote('');
    let ok = 0; let skipped = 0; let failed = 0;
    for (const id of bulkSelected) {
      try {
        const d = await fetch(`${API}/api/coa/activities/${id}/request-results`, {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        }).then((r) => r.json());
        if (d.success) ok++;
        else if (/only for Monitoring/i.test(d.message || '')) skipped++;
        else failed++;
      } catch { failed++; }
    }
    setBulkNote([
      `Results requested on ${ok} activit${ok === 1 ? 'y' : 'ies'}`,
      skipped ? `${skipped} skipped (auditing rows use Rebuttal)` : null,
      failed ? `${failed} failed` : null,
    ].filter(Boolean).join(' · ') + '.');
    setBulkSelected(new Set());
    setBulkBusy(false);
    onRefresh();
  };

  const at = templates.activity_type || ['Auditing', 'Monitoring', 'Investigations'];
  const cf = templates.compliance_or_fwa || ['Compliance', 'FWA', 'Both'];
  const af = templates.activity_frequency || ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Semi-annually', 'Annually', 'Ad-hoc'];
  const aq = templates.activity_quarter || ['Q1', 'Q2', 'Q3', 'Q4'];

  // ── Views — the Asana "My tasks" pattern. Which slice of the year is on
  // screen: the months that need you now, the future, the closed record, or
  // everything. Pure presentation over the already-loaded rows.
  const [view, setView] = React.useState('review');
  const nowYmV = new Date().toISOString().slice(0, 7);
  const rowBucket = (a) => {
    if (a.status === 'completed' || a.status === 'expired') return 'closed';
    const per = String(a.source_period || '');
    if (per && per >= nowYmV) return 'upcoming';
    if (!per && a.activity_start_date && String(a.activity_start_date).slice(0, 7) >= nowYmV) return 'upcoming';
    return 'review';
  };
  // ── Component tabs (operator order 2026-08-28): Precision COA breaks down
  // like an Excel workbook — one tab per component (ODAG, CDAG, SNPCC,
  // Reporting, Call Center, Enrollment, …) plus the COA Global View. Tabs are
  // derived from the rows on screen, so a department user, whose rows the
  // server already limits to their departments, only ever sees their own tabs.
  // The tab is decided from the measure key OR the component text, so a row
  // with and a row without a live series always land on the same tab
  // (operator report 2026-08-28: "Case Management" and "SNPCC" had split).
  // SNPCC is Case Management's work and files under Case Management.
  const componentOf = (a) => {
    const key = String(a.series_measure_key || '');
    const text = String(a.component || '');
    if (key.startsWith('odag') || /\bODAG\b/.test(text)) return 'ODAG';
    if (key.startsWith('cdag') || /\bCDAG\b/.test(text)) return 'CDAG';
    if (key.startsWith('snpcc') || /\b(SNPCC|SNPE)\b/.test(text) || /^Case Management\b/.test(text)) return 'Case Management';
    if (key.startsWith('reporter')) return 'Reporting';
    // Manual measures and free-standing rows: the component up to its first
    // dash ("Call Center — Calls answered within 30 seconds" → "Call Center").
    const comp = text.split(/\s[—–-]\s/)[0].trim();
    return comp || 'Other';
  };
  const [componentTab, setComponentTab] = React.useState('all');
  const componentCounts = new Map();
  for (const a of activities) componentCounts.set(componentOf(a), (componentCounts.get(componentOf(a)) || 0) + 1);
  const COMPONENT_TABS = [
    { key: 'all', label: 'COA Global View', count: activities.length },
    ...[...componentCounts.entries()].sort((x, y) => x[0].localeCompare(y[0])).map(([label, count]) => ({ key: label, label, count })),
  ];
  const tabActivities = componentTab === 'all' ? activities : activities.filter((a) => componentOf(a) === componentTab);
  React.useEffect(() => {
    // A tab that no longer has rows (filters, a refresh) falls back to the global view.
    if (componentTab !== 'all' && !componentCounts.has(componentTab)) setComponentTab('all');
  }, [componentTab, activities]); // eslint-disable-line react-hooks/exhaustive-deps

  const viewCounts = { review: 0, upcoming: 0, closed: 0 };
  for (const a of tabActivities) viewCounts[rowBucket(a)] += 1;
  const viewActivities = view === 'all' ? tabActivities : tabActivities.filter((a) => rowBucket(a) === view);
  const VIEWS = [
    { key: 'review', label: 'Under review', count: viewCounts.review, tip: 'Periods that have ended and are not closed — the months that need you' },
    { key: 'upcoming', label: 'Upcoming', count: viewCounts.upcoming, tip: 'Scheduled periods that have not ended yet' },
    { key: 'closed', label: 'Closed', count: viewCounts.closed, tip: 'Completed and locked, plus expired activities' },
    { key: 'all', label: 'All', count: tabActivities.length, tip: 'The whole plan, every period' },
  ];
  // The CMS-layout Excel export for the selected year's plan (or the
  // newest plan when no year filter is set).
  const exportPlan = (filters.plan_year
    ? workPlans.find((wp) => String(wp.plan_year) === String(filters.plan_year))
    : null) || (workPlans.length ? [...workPlans].sort((x, y) => y.plan_year - x.plan_year)[0] : null);

  // Operator order 2026-08-19 (evening): the measure board is GONE. The CMS
  // grid — one row per period, the CPE-format record — IS Precision COA.
  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-slate-800 tracking-tight">Precision COA</h2>
          <p className="text-slate-500 text-sm mt-0.5">The approved oversight calendar: every activity for the year, each pulling its own audit period from its source app.</p>
        </div>
        <div className="flex items-center gap-3">
          {exportPlan && (
            <a
              href={`${API}/api/coa/work-plans/${exportPlan.id}/export`}
              className="px-4 py-2 rounded-xl bg-white text-slate-700 text-sm font-medium border border-slate-200 hover:bg-slate-50 transition-colors whitespace-nowrap"
              title={`Download the ${exportPlan.plan_year} universe as a CMS-layout Excel workbook — audit-ready`}
            >
              Export universe (Excel)
            </a>
          )}
        </div>
      </div>

      {/* Component tabs — the workbook: COA Global View, then one tab per
          component the rows on screen belong to. */}
      {COMPONENT_TABS.length > 1 && (
        <div className="flex items-end gap-0.5 mb-3 border-b border-slate-200 overflow-x-auto" role="tablist" aria-label="Components">
          {COMPONENT_TABS.map((c) => (
            <button key={c.key} type="button" role="tab" aria-selected={componentTab === c.key}
              onClick={() => setComponentTab(c.key)}
              title={c.key === 'all' ? 'Every component together' : `Only ${c.label}`}
              className={`px-3.5 py-2 text-[13px] font-semibold whitespace-nowrap rounded-t-xl border border-b-0 transition-colors ${
                componentTab === c.key
                  ? 'bg-white text-slate-900 border-slate-200 -mb-px'
                  : 'bg-slate-50 text-slate-500 border-transparent hover:text-slate-800 hover:bg-slate-100'
              }`}>
              {c.label}
              <span className={`ml-1.5 text-[10px] tabular-nums px-1.5 py-px rounded-full font-semibold ${
                componentTab === c.key ? 'bg-orange-100 text-orange-700' : 'bg-slate-200 text-slate-500'
              }`}>{c.count}</span>
            </button>
          ))}
        </div>
      )}

      {/* Views — Asana segments: the months that need you, the future, the
          closed record, or everything. */}
      <div className="inline-flex mb-4 p-1 rounded-[14px] gap-0.5 bg-slate-900/[0.045] ring-1 ring-slate-900/[0.04] max-w-full overflow-x-auto">
        {VIEWS.map((v) => (
          <button key={v.key} type="button" onClick={() => setView(v.key)} title={v.tip}
            className={`relative px-3.5 py-1.5 text-[13px] font-semibold rounded-[10px] whitespace-nowrap transition-all duration-150 ${
              view === v.key ? 'bg-white text-slate-900 shadow-[0_1px_4px_rgba(15,23,42,0.12)]' : 'text-slate-500 hover:text-slate-700'
            }`}>
            <span className="flex items-center gap-1.5">
              {v.label}
              <span className={`text-[10px] tabular-nums min-w-[18px] text-center px-1 py-px rounded-full font-semibold ${
                view === v.key ? 'bg-slate-900 text-white' : 'bg-slate-200 text-slate-500'
              }`}>{v.count}</span>
            </span>
          </button>
        ))}
      </div>

      {/* No filter bar — filtering lives in the COLUMN TITLES (operator
          order 2026-08-19 evening). Only the plan-year scope and the action
          buttons live up here. */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <select
          value={filters.plan_year}
          onChange={(e) => onFiltersChange((f) => ({ ...f, plan_year: e.target.value }))}
          title="Plan year"
          className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 min-w-[100px]"
        >
          <option value="">All years</option>
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        {isCoaCompliance && (
          <>
            <input
              type="month"
              value={agentPeriod}
              onChange={(e) => setAgentPeriod(e.target.value)}
              title="The month to generate monitoring activities for. Pick the month your data (for example universe case receipt dates) actually falls in."
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
            />
            <button
              type="button"
              onClick={handleRunAgent}
              disabled={runningAgent || !/^\d{4}-\d{2}$/.test(agentPeriod)}
              title="Draft the selected month's monitoring activities from Mandate, Sentinel, and the Scrubber's universe timeliness. Privacy, Issues, and CAP oversight are never drafted automatically; add those measures in Monitoring if you want them. Lands as drafts for your review."
              className="px-4 py-2 rounded-xl bg-orange-600 text-white text-sm font-medium border border-orange-600 hover:bg-orange-700 transition-colors disabled:opacity-50"
            >
              {runningAgent ? 'Generating…' : 'Generate activities'}
            </button>
          </>
        )}
        {isCoaCompliance && bulkSelected.size > 0 && (
          <button type="button" onClick={requestResultsBulk} disabled={bulkBusy}
            title="Notify each selected activity's assignees to enter results — one click for every ticked month"
            className="px-4 py-2 rounded-xl bg-orange-600 text-white text-sm font-medium border border-orange-600 hover:bg-orange-700 transition-colors disabled:opacity-50">
            {bulkBusy ? 'Requesting…' : `Request results (${bulkSelected.size})`}
          </button>
        )}
        <button type="button" onClick={onRefresh} className="px-4 py-2 rounded-xl bg-slate-100 text-slate-700 text-sm font-medium border border-slate-200 hover:bg-slate-200 transition-colors">
          Refresh
        </button>
        {bulkNote && <span className="text-emerald-700 text-sm ml-2">{bulkNote}</span>}
        <span className="text-slate-500 text-sm ml-2">
          {viewActivities.length} of {tabActivities.length} entr{tabActivities.length === 1 ? 'y' : 'ies'}{componentTab !== 'all' ? ` in ${componentTab}` : ''}
        </span>
        {agentMsg && <span className="text-slate-500 text-sm ml-2">{agentMsg}</span>}
      </div>

      <ActivityTable
        activities={viewActivities}
        loading={loading}
        pinActions={!!fdrEntities}
        emptyText={view === 'review'
          ? 'Nothing under review — no ended period is waiting on you. Check Upcoming or All.'
          : view === 'upcoming' ? 'No upcoming periods in this plan.'
          : view === 'closed' ? 'Nothing closed yet.'
          : 'No activities for these filters yet. Build the plan in the Create tab and its rows appear here for the whole year.'}
        onViewRecord={onViewRecord}
        onViewMeasurements={onViewMeasurements}
        selectedIds={isCoaCompliance ? bulkSelected : null}
        onToggleSelect={isCoaCompliance ? toggleBulk : null}
        onSelectMany={isCoaCompliance ? selectManyBulk : null}
        renderResultEntry={(act) => (
          // Free-standing rows always take a result. A synced row (ODAG, CDAG,
          // SNPE…) takes one only while its universe has not arrived — the
          // period is still awaiting or overdue (operator ruling 2026-08-28).
          (!act.monitoring_series_id || ['awaiting', 'overdue'].includes(act.monitoring_status))
          && ['Monitoring', 'Investigations'].includes(act.activity_type)
          && !['completed', 'expired'].includes(act.status)
            ? <InlineResultEntry act={act} onSaved={onRefresh} />
            : null
        )}
        renderActions={(act) => (
          <>
            {isCoaCompliance && act.status !== 'expired' && <SyncButton act={act} onDone={onRefresh} />}
            {isCoaCompliance && act.status !== 'expired' && <ExtendPeriodsButton act={act} onDone={onRefresh} />}
            {isCoaCompliance && ['met', 'missed'].includes(act.monitoring_status) && !['completed', 'expired'].includes(act.status) && (
              <CloseMonthButton act={act} onDone={onRefresh} />
            )}
            {isCoaCompliance && !['completed', 'expired'].includes(act.status) && (
              <ExpireButton act={act} onDone={onRefresh} />
            )}
            {isCoaCompliance && ['completed', 'expired'].includes(act.status) && (
              <ReopenButton act={act} onDone={onRefresh} />
            )}
            {isCoaCompliance && (
              <button type="button" onClick={() => setEditRow(act)} className="text-slate-600 hover:text-slate-800 text-xs" title="Edit this record — saves permanently">Edit</button>
            )}
            {act.activity_type === 'Auditing' && act.status === 'completed' && onViewReport && onDownloadReport && (
              <>
                <button type="button" onClick={() => onViewReport(act.id)} className="text-orange-600 hover:text-orange-700 text-xs">Report</button>
                <button type="button" onClick={() => onDownloadReport(act.id, 'pdf')} className="text-slate-600 hover:text-slate-800 text-xs">PDF</button>
                <button type="button" onClick={() => onDownloadReport(act.id, 'docx')} className="text-slate-600 hover:text-slate-800 text-xs">Word</button>
              </>
            )}
            {isCoaCompliance && act.activity_type === 'Auditing' && (
              <button type="button" onClick={() => handleRequestRebuttal(act.id)} disabled={requestingRebuttalId === act.id} className="text-slate-600 hover:text-slate-800 text-xs disabled:opacity-50">{requestingRebuttalId === act.id ? 'Sending…' : 'Rebuttal'}</button>
            )}
            {isCoaCompliance && (act.activity_type === 'Monitoring' || act.activity_type === 'Investigations') && (
              <button type="button" onClick={() => handleRequestResults(act.id)} disabled={requestingResultsId === act.id} className="text-slate-600 hover:text-slate-800 text-xs disabled:opacity-50">{requestingResultsId === act.id ? 'Sending…' : 'Results'}</button>
            )}
          </>
        )}
      />

      {agentDetails.length > 0 && (
        <div className="mt-4 bg-white rounded-2xl border border-slate-200/60 shadow-sm px-4 py-3">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Run results by source</div>
          <ul className="space-y-1">
            {agentDetails.map((r, i) => (
              <li key={i} className="text-sm flex items-start gap-2">
                <span className={`mt-1 inline-block w-2 h-2 rounded-full flex-shrink-0 ${r.tone === 'ok' ? 'bg-emerald-500' : r.tone === 'skip' ? 'bg-amber-400' : 'bg-red-500'}`} />
                <span className="text-slate-700"><span className="font-medium">{r.app}:</span> {r.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {editRow && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={() => setEditRow(null)}>
          <div className="w-[min(64rem,95vw)] max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <ActivityEditForm
              activity={editRow}
              departments={departments}
              fdrEntities={fdrEntities}
              fdrOptions={fdrOptions}
              templates={templates}
              onSave={() => { setEditRow(null); onRefresh(); }}
              onCancel={() => setEditRow(null)}
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function AuditTrailPanel({ activity, loading, logs }) {
  return (
    <div className="mt-6 bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6">
      <div className="flex justify-between items-start gap-4">
        <div>
          <h3 className="font-medium text-slate-800">Audit trail</h3>
          <p className="text-xs text-slate-500 mt-0.5">Immutable log of who changed this activity and when.</p>
        </div>
        <div className="text-xs text-slate-500 text-right">
          <div><span className="font-medium">Created:</span> {formatCmsDateTime(activity.created_at) || '—'}</div>
          <div><span className="font-medium">Updated:</span> {formatCmsDateTime(activity.updated_at) || '—'}</div>
        </div>
      </div>

      {loading && <div className="py-6 text-slate-500 text-sm">Loading audit trail…</div>}
      {!loading && (!logs || logs.length === 0) && (
        <div className="py-6 text-slate-500 text-sm">No audit entries yet.</div>
      )}
      {!loading && Array.isArray(logs) && logs.length > 0 && (
        <ul className="mt-4 space-y-2">
          {logs.map((l) => (
            <li key={l.id} className="p-3 rounded-xl border border-slate-100 bg-slate-50/50">
              <div className="text-xs text-slate-500 flex flex-wrap gap-x-2 gap-y-0.5">
                <span className="font-medium text-slate-700">{formatCmsDateTime(l.created_at) || isoToCmsDate(l.created_at) || '—'}</span>
                <span>•</span>
                <span className="font-medium text-slate-700">{l.user_name || 'User'}</span>
                <span>•</span>
                <span className="uppercase tracking-wide">{l.action_type}</span>
              </div>
              {l.description && <div className="text-sm text-slate-700 mt-1">{l.description}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Work tab: saved activities in universe-style table (same columns as Universe tab)
// A recurring activity covers one period per row. This button materializes
// the remaining plan-year periods for rows created before recurrence existed
// (or after months were deleted). Idempotent server-side — existing months
// are skipped, so pressing it twice is safe. Series-generated rows refuse it
// (the engine owns their periods).
// ── Inline result entry — the department assignee enters the metric right
// in the grid (operator order 2026-08-19 evening: "the department owner must
// have the ability to enter the score"). Renders for series-less Monitoring/
// Investigations rows that are not completed; the SERVER decides who may
// save (compliance, series owner, or assignee), so showing the box to a
// non-eligible user just earns a clear refusal, never a silent write.
/** How each unit reads in the entry box. */
const UNIT_WORD = {
  percent: { label: 'Percent', suffix: '%' },
  seconds: { label: 'Seconds', suffix: 'seconds' },
  minutes: { label: 'Minutes', suffix: 'minutes' },
  hours: { label: 'Hours', suffix: 'hours' },
  days: { label: 'Days', suffix: 'days' },
  count: { label: 'Count', suffix: '' },
};

/** A benchmark shown the way it was entered: 95%, 2:00, 30 days, or a count. */
function fmtBenchmark(v, unit) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  if (unit === 'seconds') return `${Math.floor(Math.round(n) / 60)}:${String(Math.round(n) % 60).padStart(2, '0')}`;
  const round = Math.round(n * 100) / 100;
  if (unit === 'minutes') return `${round} minute${round === 1 ? '' : 's'}`;
  if (unit === 'hours') return `${round} hour${round === 1 ? '' : 's'}`;
  if (unit === 'days') return `${round} day${round === 1 ? '' : 's'}`;
  if (unit === 'count') return String(Math.round(n));
  return `${Math.round(n * 10) / 10}%`;
}

function InlineResultEntry({ act, onSaved }) {
  const [num, setNum] = useState('');
  const [den, setDen] = useState('');
  const [val, setVal] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  // A planned row (a series) is measured against the MEASURE's benchmark;
  // a free-standing row against its own. Either way the person enters the
  // result itself — 94 against 95%, 1:27 against 2:00 — never "x out of y"
  // when a benchmark exists (operator ruling 2026-08-28).
  const bm = act.monitoring_series_id
    ? { benchmark: act.series_benchmark, benchmark_unit: act.series_benchmark_unit || 'percent', benchmark_direction: act.series_direction || 'higher' }
    : { benchmark: act.benchmark, benchmark_unit: act.benchmark_unit, benchmark_direction: act.benchmark_direction };
  const hasBenchmark = bm.benchmark !== null && bm.benchmark !== undefined && bm.benchmark !== '';
  const submit = async () => {
    if (busy) return;
    setBusy(true); setMsg(null);
    try {
      const res = await fetch(`${API}/api/coa/monitoring/activities/${act.id}/result`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(hasBenchmark
          ? { value: Number(val) }
          : { numerator: Number(num), denominator: Number(den) }),
      });
      const data = await res.json();
      if (!data.success) { setMsg({ tone: 'err', text: data.message || 'Could not save' }); setBusy(false); return; }
      setMsg({ tone: 'ok', text: 'Result recorded.' });
      setBusy(false);
      onSaved && onSaved();
    } catch (e) { setMsg({ tone: 'err', text: e.message }); setBusy(false); }
  };
  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white px-4 py-3">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">
        Enter this period's result
        {act.monitoring_series_id && (
          <span className="ml-2 normal-case tracking-normal font-normal text-slate-500">
            No universe received for this period? Enter the count by hand. A synced universe replaces it.
          </span>
        )}
        {hasBenchmark && (
          <span className="ml-2 normal-case tracking-normal font-normal text-slate-500">
            Benchmark {fmtBenchmark(bm.benchmark, bm.benchmark_unit)}
            {' · '}{bm.benchmark_direction === 'lower' ? 'at or below passes' : 'at or above passes'}
          </span>
        )}
      </div>
      {hasBenchmark ? (
        // The row states what it is measured against, so the officer or the
        // department owner enters the measure itself.
        <div className="flex items-center gap-2 flex-wrap">
          <input type="number" min="0" step="0.01" inputMode="decimal"
            placeholder={UNIT_WORD[bm.benchmark_unit] ? UNIT_WORD[bm.benchmark_unit].label : 'Percent'}
            value={val} onChange={(e) => setVal(e.target.value)}
            className="w-40 rounded-lg border-slate-200 text-sm py-1.5" />
          <span className="text-sm text-slate-500">
            {UNIT_WORD[bm.benchmark_unit] ? UNIT_WORD[bm.benchmark_unit].suffix : '%'}
          </span>
          <button type="button" onClick={submit} disabled={busy || val === ''}
            className="px-4 py-1.5 rounded-lg bg-gradient-to-r from-orange-600 to-amber-500 text-white text-sm font-semibold disabled:opacity-50 hover:opacity-90">
            {busy ? 'Saving…' : 'Save result'}
          </button>
          {val !== '' && Number.isFinite(Number(val)) && (
            <span className={`text-xs font-semibold ${
              (bm.benchmark_direction === 'lower' ? Number(val) <= Number(bm.benchmark) : Number(val) >= Number(bm.benchmark))
                ? 'text-emerald-700' : 'text-rose-600'}`}>
              {fmtBenchmark(val, bm.benchmark_unit)}
              {' · '}
              {(bm.benchmark_direction === 'lower' ? Number(val) <= Number(bm.benchmark) : Number(val) >= Number(bm.benchmark))
                ? 'meets the benchmark' : 'below the benchmark'}
            </span>
          )}
          {msg && <span className={`text-xs ${msg.tone === 'ok' ? 'text-emerald-700' : 'text-rose-600'}`}>{msg.text}</span>}
        </div>
      ) : (
        <div className="flex items-center gap-2 flex-wrap">
          <input type="number" min="0" inputMode="numeric" placeholder="Met the standard" value={num}
            onChange={(e) => setNum(e.target.value)} className="w-36 rounded-lg border-slate-200 text-sm py-1.5" />
          <span className="text-sm text-slate-500">out of</span>
          <input type="number" min="1" inputMode="numeric" placeholder="Total" value={den}
            onChange={(e) => setDen(e.target.value)} className="w-28 rounded-lg border-slate-200 text-sm py-1.5" />
          <button type="button" onClick={submit} disabled={busy || num === '' || den === '' || Number(num) > Number(den)}
            className="px-4 py-1.5 rounded-lg bg-gradient-to-r from-orange-600 to-amber-500 text-white text-sm font-semibold disabled:opacity-50 hover:opacity-90">
            {busy ? 'Saving…' : 'Save result'}
          </button>
          {num !== '' && den !== '' && Number(den) > 0 && Number(num) <= Number(den) && (
            <span className="text-xs text-slate-500">{Math.round((Number(num) / Number(den)) * 1000) / 10}%</span>
          )}
          {msg && <span className={`text-xs ${msg.tone === 'ok' ? 'text-emerald-700' : 'text-rose-600'}`}>{msg.text}</span>}
        </div>
      )}
    </div>
  );
}

function ExtendPeriodsButton({ act, onDone }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  if (!/^(month|quarter|semi)/i.test(act.activity_frequency || '')) return null;
  const run = () => {
    if (busy) return;
    setBusy(true); setNote('');
    fetch(`${API}/api/coa/activities/${act.id}/extend-periods`, { method: 'POST', credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        setBusy(false);
        // Say EXACTLY what happened — "nothing visible changed" reads as
        // broken when the truth is "the year already exists" (live-found
        // 2026-08-19: the periods were there, the message was too quiet).
        if (d.success) {
          setNote(d.created
            ? `+${d.created} ${d.period_word || 'month'}${d.created === 1 ? '' : 's'} added`
            : d.skipped
              ? `all ${d.skipped} ${d.period_word || 'month'}${d.skipped === 1 ? '' : 's'} already exist — check your filters/view`
              : (d.reason || 'no periods remain in this plan year'));
          if (d.created) onDone();
        } else setNote(d.message || 'failed');
      })
      .catch(() => { setBusy(false); setNote('failed'); });
  };
  return (
    <button type="button" onClick={run} disabled={busy}
      title="Create this activity for each remaining period of the plan year. Months that already exist are skipped."
      className="text-slate-600 hover:text-slate-800 text-xs disabled:opacity-50 whitespace-nowrap">
      {busy ? 'Adding…' : note || 'Add months'}
    </button>
  );
}

function WorkActivitiesTable({ activities, fdrEntities = null, selectedActivity, setSelectedActivity, setEditingActivity, onRefresh, templates, departments, isCoaCompliance = true, onViewReport, onDownloadReport, onViewMeasurements, onViewRecord }) {

  const handleComplete = (act) => {
    if (!window.confirm('Mark this activity as completed? Deficiencies will be locked.')) return;
    fetch(`${API}/api/coa/activities/${act.id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'completed' })
    })
      .then((r) => r.json())
      .then((d) => { if (d.success) onRefresh(); });
  };

  const handleDelete = (act) => {
    const msg = act.monitoring_series_id
      ? 'Delete this monitoring entry? It belongs to an active monitoring series, so the next refresh will regenerate this period unless the series itself is deleted in the Monitoring tab.'
      : 'Delete this activity? This cannot be undone.';
    if (!window.confirm(msg)) return;
    fetch(`${API}/api/coa/activities/${act.id}`, { method: 'DELETE', credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { if (d.success) onRefresh(); else window.alert(d.message || 'Delete failed.'); });
  };

  return (
    <div className="mb-6">
      <ActivityTable
        activities={activities}
        loading={false}
        emptyText="No activities in this work plan yet. Use + Add activity or Generate activities."
        onViewRecord={onViewRecord}
        onViewMeasurements={onViewMeasurements}
        renderActions={(act) => (
          <>
            {isCoaCompliance && (
              <>
                <button type="button" onClick={() => setEditingActivity(act)} className="text-slate-600 hover:text-slate-800 font-medium text-xs">Edit</button>
                {act.status !== 'completed' && (
                  <button type="button" onClick={() => handleComplete(act)} className="text-orange-600 hover:text-orange-700 font-medium text-xs">Complete</button>
                )}
                {act.status === 'completed' && <ReopenButton act={act} onDone={onRefresh} />}
                <button type="button" onClick={() => handleDelete(act)} className="text-red-600 hover:text-red-700 font-medium text-xs">Delete</button>
              </>
            )}
            <button type="button" onClick={() => setSelectedActivity(selectedActivity && selectedActivity.id === act.id ? null : act)} className="text-slate-600 hover:text-slate-800 text-xs whitespace-nowrap" title="Show the itemized findings under this activity">Deficiencies</button>
            {act.activity_type === 'Auditing' && act.status === 'completed' && onViewReport && onDownloadReport && (
              <>
                <button type="button" onClick={() => onViewReport(act.id)} className="text-orange-600 hover:text-orange-700 font-medium text-xs">Report</button>
                <button type="button" onClick={() => onDownloadReport(act.id, 'pdf')} className="text-slate-600 hover:text-slate-800 text-xs">PDF</button>
                <button type="button" onClick={() => onDownloadReport(act.id, 'docx')} className="text-slate-600 hover:text-slate-800 text-xs">Word</button>
              </>
            )}
          </>
        )}
      />
    </div>
  );
}

function ActivityEditForm({ activity, departments, fdrEntities = null, fdrOptions = [], templates, onSave, onCancel }) {
  // A monitoring row is measured against its MEASURE's benchmark (the series),
  // so that is what the form shows and what saving changes — for every open
  // month of the measure. Free-standing rows keep their own benchmark.
  const isSeriesRow = !!activity.monitoring_series_id;
  const [form, setForm] = useState(() => ({
    ...activity,
    ...(isSeriesRow ? {
      benchmark: activity.series_benchmark ?? '',
      benchmark_unit: activity.series_benchmark_unit || 'percent',
      benchmark_direction: activity.series_direction || 'higher',
    } : {}),
    activity_start_date: isoToCmsDate(activity.activity_start_date),
    activity_completion_date: isoToCmsDate(activity.activity_completion_date),
    department_ids: (activity.departments || []).map((d) => d.id),
    assignee_user_ids: (activity.assignees || []).map((u) => u.id)
  }));
  const [saving, setSaving] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState([]);

  const at = templates.activity_type || ['Auditing', 'Monitoring', 'Investigations'];
  const cf = templates.compliance_or_fwa || ['Compliance', 'FWA', 'Both'];
  const af = templates.activity_frequency || ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Semi-annually', 'Annually', 'Ad-hoc'];
  const aq = templates.activity_quarter || ['Q1', 'Q2', 'Q3', 'Q4'];
  const deptIds = Array.isArray(form.department_ids) ? form.department_ids : (form.departments || []).map((d) => d.id);

  useEffect(() => {
    if (!deptIds.length) { setAssignableUsers([]); return; }
    const seen = new Set();
    Promise.all(deptIds.map((did) => fetch(`${API}/api/users/by-department/${did}`, { credentials: 'include' }).then((r) => r.json()).then((d) => d.users || [])))
      .then((arrays) => {
        const merged = [];
        for (const arr of arrays) {
          for (const u of arr) {
            if (u.id && !seen.has(u.id)) { seen.add(u.id); merged.push(u); }
          }
        }
        setAssignableUsers(merged);
      })
      .catch(() => setAssignableUsers([]));
  }, [deptIds.join(',')]);

  const toggleDept = (id) => {
    setForm((f) => {
      const ids = Array.isArray(f.department_ids) ? f.department_ids : (f.departments || []).map((d) => d.id);
      return { ...f, department_ids: ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id] };
    });
  };

  const handleSave = () => {
    const startIso = cmsToIsoDate(form.activity_start_date);
    if (!startIso) {
      alert('Start date must be in YYYY/MM/DD format.');
      return;
    }
    const completionIso = form.activity_completion_date ? cmsToIsoDate(form.activity_completion_date) : null;
    if (form.activity_completion_date && !completionIso) {
      alert('Completion date must be in YYYY/MM/DD format (or leave blank for TBD).');
      return;
    }
    const payload = {
      component: form.component,
      entity_name: form.entity_name || null,
      department_ids: deptIds,
      assignee_user_ids: Array.isArray(form.assignee_user_ids) ? form.assignee_user_ids : [],
      activity_type: form.activity_type,
      compliance_or_fwa: form.compliance_or_fwa,
      activity_frequency: form.activity_frequency,
      activity_quarter: form.activity_quarter || null,
      activity_rationale: form.activity_rationale,
      activity_description: form.activity_description,
      activity_start_date: startIso,
      activity_completion_date: completionIso,
      number_of_deficiencies: form.number_of_deficiencies ?? null,
      description_of_deficiencies: form.description_of_deficiencies || null,
      remediation_activities: form.remediation_activities || null,
      benchmark: form.benchmark === '' || form.benchmark === undefined ? null : form.benchmark,
      benchmark_unit: form.benchmark_unit || 'percent',
      benchmark_direction: form.benchmark_direction || 'higher',
      corrective_action_required: form.corrective_action_required || null,
      activity_results_shared: form.activity_results_shared || null,
      cfr_references: form.cfr_references || null,
      chapter_references: form.chapter_references || null,
      status: form.status
    };
    setSaving(true);
    fetch(`${API}/api/coa/activities/${activity.id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then((r) => r.json())
      .then((d) => {
        setSaving(false);
        if (d.success) onSave();
        else alert(d.message || 'Update failed');
      })
      .catch(() => setSaving(false));
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6 mb-6">
      <h3 className="text-lg font-bold text-slate-800 mb-4">Edit activity — add completion date, deficiencies count, description as you have them</h3>
      <div className="mb-4 p-3 rounded-xl bg-slate-50/50 border border-slate-200/60 text-xs text-slate-600 flex flex-wrap gap-x-4 gap-y-1">
        <span><span className="font-medium">Created:</span> {formatCmsDateTime(activity.created_at) || '—'}</span>
        <span><span className="font-medium">Last updated:</span> {formatCmsDateTime(activity.updated_at) || '—'}</span>
        {activity.created_cap_id && <span><span className="font-medium">CAP:</span> {activity.created_cap_id}</span>}
      </div>
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <label className="text-slate-500 block mb-1">Component</label>
          <input value={form.component || ''} onChange={(e) => setForm((f) => ({ ...f, component: e.target.value }))} className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" />
        </div>
        <div>
          <label className="text-slate-500 block mb-1">Entity name</label>
          {fdrEntities ? (
            <select value={form.entity_name || ''} onChange={(e) => setForm((f) => ({ ...f, entity_name: e.target.value }))} className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400">
              <option value="">Pick the FDR…</option>
              {fdrEntities.map((en) => <option key={en.id} value={en.name}>{en.name}</option>)}
            </select>
          ) : (
            <>
              <input value={form.entity_name || ''} onChange={(e) => setForm((f) => ({ ...f, entity_name: e.target.value }))} placeholder="FDR or internal dept" list={fdrOptions.length ? 'coa-fdr-options-edit' : undefined} className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" />
              {fdrOptions.length > 0 && (
                <datalist id="coa-fdr-options-edit">
                  {fdrOptions.map((en) => <option key={en.id} value={en.name} />)}
                </datalist>
              )}
            </>
          )}
        </div>
        <div className="col-span-2">
          <label className="text-slate-500 block mb-1">Departments</label>
          <div className="flex flex-wrap gap-2">
            {departments.map((d) => (
              <label key={d.id} className="flex items-center gap-1 text-xs">
                <input type="checkbox" checked={deptIds.includes(d.id)} onChange={() => toggleDept(d.id)} className="rounded" />
                {d.name}
              </label>
            ))}
          </div>
        </div>
        <div className="col-span-2">
          <label className="text-slate-500 block mb-1">Assigned users (for rebuttal requests — up to 3 per department)</label>
          {assignableUsers.length === 0 && deptIds.length > 0 && <p className="text-xs text-slate-400 mb-1">Loading users for selected departments…</p>}
          {assignableUsers.length === 0 && deptIds.length === 0 && <p className="text-xs text-slate-400 mb-1">Select departments above to choose assignees.</p>}
          <div className="flex flex-wrap gap-3">
            {assignableUsers.map((u) => (
              <label key={u.id} className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={(form.assignee_user_ids || []).includes(u.id)}
                  onChange={() => setForm((f) => {
                    const ids = f.assignee_user_ids || [];
                    return { ...f, assignee_user_ids: ids.includes(u.id) ? ids.filter((x) => x !== u.id) : [...ids, u.id] };
                  })}
                  className="rounded"
                />
                {[u.name, u.email].filter(Boolean).join(' · ')}
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="text-slate-500 block mb-1">Activity type</label>
          <select value={form.activity_type} onChange={(e) => setForm((f) => ({ ...f, activity_type: e.target.value }))} className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400">
            {at.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div>
          <label className="text-slate-500 block mb-1">Compliance or FWA</label>
          <select value={form.compliance_or_fwa} onChange={(e) => setForm((f) => ({ ...f, compliance_or_fwa: e.target.value }))} className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400">
            {cf.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div>
          <label className="text-slate-500 block mb-1">Frequency</label>
          <select value={form.activity_frequency} onChange={(e) => setForm((f) => ({ ...f, activity_frequency: e.target.value }))} className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400">
            {af.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div>
          <label className="text-slate-500 block mb-1">Quarter</label>
          <select value={form.activity_quarter || ''} onChange={(e) => setForm((f) => ({ ...f, activity_quarter: e.target.value }))} className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400">
            {aq.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div>
          <label className="text-slate-500 block mb-1">Start date</label>
          <input
            type="text"
            inputMode="numeric"
            placeholder="YYYY/MM/DD"
            value={form.activity_start_date || ''}
            onChange={(e) => setForm((f) => ({ ...f, activity_start_date: e.target.value }))}
            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
          />
        </div>
        <div>
          <label className="text-slate-500 block mb-1">Completion date (TBD until known)</label>
          <input
            type="text"
            inputMode="numeric"
            placeholder="YYYY/MM/DD or blank (TBD)"
            value={form.activity_completion_date || ''}
            onChange={(e) => setForm((f) => ({ ...f, activity_completion_date: e.target.value }))}
            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
          />
        </div>
        <div>
          <label className="text-slate-500 block mb-1">Number of deficiencies (TBD until known)</label>
          <input type="number" min={0} value={form.number_of_deficiencies ?? ''} onChange={(e) => setForm((f) => ({ ...f, number_of_deficiencies: e.target.value === '' ? null : parseInt(e.target.value, 10) }))} placeholder="TBD" className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" />
        </div>
        <div>
          <label className="text-slate-500 block mb-1">Status</label>
          <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400">
            <option value="draft">Draft</option>
            <option value="in_progress">In progress</option>
            <option value="completed">Completed</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className="text-slate-500 block mb-1">Description of deficiencies (TBD until known)</label>
          <textarea value={form.description_of_deficiencies || ''} onChange={(e) => setForm((f) => ({ ...f, description_of_deficiencies: e.target.value }))} placeholder="TBD or NA" rows={3} className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm resize-y min-h-[5rem] focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" />
        </div>
        <div className="col-span-2">
          <label className="text-slate-500 block mb-1">Remediation activities</label>
          <textarea value={form.remediation_activities || ''} onChange={(e) => setForm((f) => ({ ...f, remediation_activities: e.target.value }))} placeholder="For monitoring/SIU: metric, threshold. For auditing: what was done to correct the deficiency." rows={3} className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm resize-y min-h-[5rem] focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" />
        </div>
        {/* The standard this activity is measured against. On the edit form as
            well as on Add activity, so an activity created before the field
            existed can still be given its benchmark. */}
        <div className="col-span-2">
          <label className="text-slate-500 block mb-1">Benchmark</label>
          {isSeriesRow && (
            <p className="text-xs text-slate-500 mb-1.5">
              This benchmark belongs to the measure. Saving it applies to every open month of this measure and re-scores them; closed months are not changed.
            </p>
          )}
          <div className="flex gap-2 items-center flex-wrap">
            <input type="number" min="0" step="0.1" placeholder="e.g. 95" value={form.benchmark ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, benchmark: e.target.value }))}
              className="w-32 rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" />
            <select value={form.benchmark_unit || 'percent'}
              onChange={(e) => setForm((f) => ({ ...f, benchmark_unit: e.target.value }))}
              className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400">
              <option value="percent">percent</option>
              <option value="seconds">seconds</option>
              <option value="minutes">minutes</option>
              <option value="hours">hours</option>
              <option value="days">days</option>
              <option value="count">count</option>
            </select>
            <select value={form.benchmark_direction || 'higher'}
              onChange={(e) => setForm((f) => ({ ...f, benchmark_direction: e.target.value }))}
              className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400">
              <option value="higher">At or above passes</option>
              <option value="lower">At or below passes</option>
            </select>
            <span className="text-xs text-slate-400">Leave blank and the period passes only when there are no deficiencies.</span>
          </div>
        </div>
        <div className="col-span-2">
          <label className="text-slate-500 block mb-1">Rationale</label>
          <textarea value={form.activity_rationale || ''} onChange={(e) => setForm((f) => ({ ...f, activity_rationale: e.target.value }))} rows={3} className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm resize-y min-h-[5rem] focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" />
        </div>
        <div className="col-span-2">
          <label className="text-slate-500 block mb-1">Activity description</label>
          <textarea value={form.activity_description || ''} onChange={(e) => setForm((f) => ({ ...f, activity_description: e.target.value }))} rows={3} className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm resize-y min-h-[5rem] focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" />
        </div>
        <div>
          <label className="text-slate-500 block mb-1">Corrective action required?</label>
          <select value={form.corrective_action_required || 'TBD'} onChange={(e) => setForm((f) => ({ ...f, corrective_action_required: e.target.value }))} className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400">
            <option value="N">N</option>
            <option value="Y">Y</option>
            <option value="TBD">TBD</option>
          </select>
        </div>
        <div>
          <label className="text-slate-500 block mb-1">Results shared?</label>
          <input type="text" value={form.activity_results_shared || ''} onChange={(e) => setForm((f) => ({ ...f, activity_results_shared: e.target.value }))} placeholder="TBD or Y/N + name" className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" />
        </div>
        <div>
          <label className="text-slate-500 block mb-1">CFR references</label>
          <textarea value={form.cfr_references || ''} onChange={(e) => setForm((f) => ({ ...f, cfr_references: e.target.value }))} rows={2} placeholder="e.g. 42 CFR §422.564" className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm resize-y min-h-[4rem] focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" />
        </div>
        <div>
          <label className="text-slate-500 block mb-1">Chapter references</label>
          <textarea value={form.chapter_references || ''} onChange={(e) => setForm((f) => ({ ...f, chapter_references: e.target.value }))} rows={2} placeholder="e.g. MA Chapter 2" className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm resize-y min-h-[4rem] focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" />
        </div>
      </div>
      <div className="mt-4 flex gap-2">
        <button type="button" onClick={handleSave} disabled={saving} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white font-medium shadow-sm shadow-orange-500/25 hover:opacity-90 disabled:opacity-50 transition-all">Save</button>
        <button type="button" onClick={onCancel} className="px-5 py-2.5 rounded-xl bg-slate-100 text-slate-700 border border-slate-200 font-medium hover:bg-slate-200 transition-colors">Cancel</button>
      </div>
    </div>
  );
}

function ActivityForm({ workPlanId, departments, fdrEntities = null, fdrOptions = [], templates, onSave, onCancel }) {
  const { active: impersonating } = useImpersonation();
  const [form, setForm] = useState({
    department_ids: [],
    assignee_user_ids: [],
    component: '',
    entity_name: '',
    activity_type: 'Auditing',
    compliance_or_fwa: 'Compliance',
    activity_frequency: 'Quarterly',
    activity_quarter: 'Q1',
    activity_rationale: '',
    activity_description: '',
    activity_start_date: isoToCmsDate(new Date().toISOString().slice(0, 10)),
    activity_completion_date: null,
    number_of_deficiencies: null,
    description_of_deficiencies: null,
    remediation_activities: null,
    benchmark: '',
    benchmark_unit: 'percent',
    benchmark_direction: 'higher',
    corrective_action_required: 'TBD',
    activity_results_shared: '',
    cfr_references: '',
    chapter_references: ''
  });
  const [saving, setSaving] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState([]);
  // AI drafting of the two prose columns (E Rationale, F Description). The
  // draft is written from the facts already typed in this row and lands in
  // the editable fields — the user always reviews before saving.
  const [drafting, setDrafting] = useState(false);
  const [draftErr, setDraftErr] = useState(null);
  const draftWithAI = async () => {
    if (!form.component.trim() || drafting) return;
    setDrafting(true); setDraftErr(null);
    try {
      const res = await fetch(`${API}/api/coa/ai/draft-activity`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          component: form.component,
          entity_name: form.entity_name,
          activity_type: form.activity_type,
          compliance_or_fwa: form.compliance_or_fwa,
          activity_frequency: form.activity_frequency,
          activity_quarter: form.activity_quarter,
          departments: departments.filter((d) => form.department_ids.includes(d.id)).map((d) => d.name),
        }),
      });
      const data = await res.json();
      if (!data.success) { setDraftErr(data.message || 'Could not draft — write them by hand.'); }
      else setForm((f) => ({ ...f, activity_rationale: data.rationale, activity_description: data.description }));
    } catch {
      setDraftErr('Could not draft — write them by hand.');
    }
    setDrafting(false);
  };
  const [columnWidths, setColumnWidths] = useState(() => {
    const w = {};
    COA_COLUMNS.forEach((c) => { w[c.key] = c.defaultWidth; });
    return w;
  });
  const [resizing, setResizing] = useState(null);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartWidth, setResizeStartWidth] = useState(0);

  const handleResizeStart = (key, e) => {
    e.preventDefault();
    setResizing(key);
    setResizeStartX(e.clientX);
    setResizeStartWidth(columnWidths[key] ?? COA_COLUMNS.find((c) => c.key === key)?.defaultWidth ?? 120);
  };

  useEffect(() => {
    if (!resizing) return;
    const onMove = (e) => {
      const newWidth = Math.max(72, resizeStartWidth + (e.clientX - resizeStartX));
      setColumnWidths((prev) => ({ ...prev, [resizing]: newWidth }));
    };
    const onUp = () => setResizing(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [resizing, resizeStartX, resizeStartWidth]);

  const handleDepartmentToggle = (deptId) => {
    setForm((f) => ({
      ...f,
      department_ids: f.department_ids.includes(deptId)
        ? f.department_ids.filter((id) => id !== deptId)
        : [...f.department_ids, deptId]
    }));
  };

  useEffect(() => {
    if (!form.department_ids?.length) { setAssignableUsers([]); return; }
    const seen = new Set();
    Promise.all(form.department_ids.map((did) => fetch(`${API}/api/users/by-department/${did}`, { credentials: 'include' }).then((r) => r.json()).then((d) => d.users || [])))
      .then((arrays) => {
        const merged = [];
        for (const arr of arrays) {
          for (const u of arr) {
            if (u.id && !seen.has(u.id)) { seen.add(u.id); merged.push(u); }
          }
        }
        setAssignableUsers(merged);
      })
      .catch(() => setAssignableUsers([]));
  }, [form.department_ids?.length ? form.department_ids.join(',') : '']);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.component.trim() || !form.activity_rationale.trim() || !form.activity_description.trim()) return;
    const startIso = cmsToIsoDate(form.activity_start_date);
    if (!startIso) {
      alert('Start date must be in YYYY/MM/DD format.');
      return;
    }
    setSaving(true);
    fetch(`${API}/api/coa/work-plans/${workPlanId}/activities`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...form,
        department_ids: form.department_ids.length ? form.department_ids : null,
        assignee_user_ids: form.assignee_user_ids || [],
        entity_name: form.entity_name || null,
        activity_quarter: form.activity_quarter || null,
        activity_start_date: startIso,
        activity_completion_date: form.activity_completion_date || null,
        number_of_deficiencies: form.number_of_deficiencies ?? null,
        description_of_deficiencies: form.description_of_deficiencies || null,
        remediation_activities: form.remediation_activities || null,
        // Blank means no benchmark, which keeps the prior rule for this row.
        benchmark: form.benchmark === '' || form.benchmark === null ? null : form.benchmark,
        benchmark_unit: form.benchmark_unit || 'percent',
        benchmark_direction: form.benchmark_direction || 'higher',
        activity_results_shared: form.activity_results_shared || null,
        cfr_references: form.cfr_references || null,
        chapter_references: form.chapter_references || null
      })
    })
      .then((r) => r.json())
      .then((d) => { if (d.success) onSave(); else alert(d.message || 'Failed'); setSaving(false); })
      .catch(() => setSaving(false));
  };

  const at = templates.activity_type || ['Auditing', 'Monitoring', 'Investigations'];
  const cf = templates.compliance_or_fwa || ['Compliance', 'FWA', 'Both'];
  const af = templates.activity_frequency || ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Semi-annually', 'Annually', 'Ad-hoc'];
  const aq = templates.activity_quarter || ['Q1', 'Q2', 'Q3', 'Q4'];

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden mb-6">
      <div className="px-5 py-4 border-b border-slate-200/60 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-lg font-bold text-slate-800 tracking-tight">Add activity</h3>
          <p className="text-slate-500 text-sm mt-0.5">Universe layout — one row per activity. Complete all required columns (A–L). Drag column borders to expand or contract.</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <button type="button" onClick={draftWithAI} disabled={drafting || !form.component.trim()}
            title={form.component.trim() ? 'Writes the Rationale and Description from what you have entered — you review and edit before saving.' : 'Enter the Component first — the draft is written from it.'}
            className="px-3.5 py-2 rounded-xl text-sm font-semibold border border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100 disabled:opacity-50 transition-colors whitespace-nowrap">
            {drafting ? 'Drafting…' : '✨ Draft rationale & description'}
          </button>
          {draftErr && <span className="text-xs text-rose-600">{draftErr}</span>}
        </div>
      </div>
      <div className="overflow-auto coa-table-scroll">
        <table className="w-full text-sm table-fixed coa-sticky-table" style={{ minWidth: COA_COLUMNS.reduce((s, c) => s + (columnWidths[c.key] || c.defaultWidth), 0) }}>
          <colgroup>
            {COA_COLUMNS.map((col) => (
              <col key={col.key} style={{ width: columnWidths[col.key] || col.defaultWidth, minWidth: 72 }} />
            ))}
          </colgroup>
          <thead>
            <tr className="bg-slate-50/50 border-b border-slate-200/60 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
              {COA_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className="py-3 px-3 relative select-none"
                  style={{ width: columnWidths[col.key] || col.defaultWidth, minWidth: 72 }}
                >
                  <HeaderWithTooltip label={col.label} tooltip={col.tooltip} allowWrap />
                  <div
                    role="separator"
                    aria-label={`Resize ${col.label}`}
                    className={`absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-orange-400 active:bg-orange-500 ${resizing === col.key ? 'bg-orange-500' : ''}`}
                    style={{ touchAction: 'none' }}
                    onMouseDown={(e) => handleResizeStart(col.key, e)}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-slate-50">
              <td className="py-2 px-3 align-top overflow-hidden">
                <input type="text" maxLength={100} placeholder="Component" value={form.component} onChange={(e) => setForm((f) => ({ ...f, component: e.target.value }))} className="w-full rounded-xl border border-slate-200 px-2.5 py-2 text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" title={COA_COLUMNS[0].tooltip} required />
              </td>
              <td className="py-2 px-3 align-top overflow-hidden">
                {fdrEntities ? (
                  <select value={form.entity_name || ''} onChange={(e) => setForm((f) => ({ ...f, entity_name: e.target.value }))} className="w-full rounded-xl border border-slate-200 px-2.5 py-2 text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" title={COA_COLUMNS[1].tooltip}>
                    <option value="">Pick the FDR…</option>
                    {fdrEntities.map((en) => <option key={en.id} value={en.name}>{en.name}</option>)}
                  </select>
                ) : (
                  <>
                    <input type="text" maxLength={200} placeholder="FDR or internal dept name" value={form.entity_name} onChange={(e) => setForm((f) => ({ ...f, entity_name: e.target.value }))} list={fdrOptions.length ? 'coa-fdr-options-create' : undefined} className="w-full rounded-xl border border-slate-200 px-2.5 py-2 text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" title={COA_COLUMNS[1].tooltip} />
                    {fdrOptions.length > 0 && (
                      <datalist id="coa-fdr-options-create">
                        {fdrOptions.map((en) => <option key={en.id} value={en.name} />)}
                      </datalist>
                    )}
                  </>
                )}
              </td>
              <td className="py-2 px-3 align-top">
                <div className="min-h-[2.5rem] max-h-28 overflow-y-auto border border-slate-200 rounded-xl p-2 bg-white shadow-sm" title={COA_COLUMNS[2].tooltip}>
                  {departments.map((d) => (
                    <label key={d.id} className="flex items-center gap-2 py-0.5 cursor-pointer text-sm">
                      <input type="checkbox" checked={form.department_ids.includes(d.id)} onChange={() => handleDepartmentToggle(d.id)} className="rounded flex-shrink-0" />
                      <span className="break-words">{d.name}</span>
                    </label>
                  ))}
                  {departments.length === 0 && <span className="text-slate-400 text-xs">No departments</span>}
                </div>
              </td>
              <td className="py-2 px-3 align-top overflow-hidden">
                <select value={form.activity_type} onChange={(e) => setForm((f) => ({ ...f, activity_type: e.target.value }))} className="w-full rounded-xl border border-slate-200 px-2.5 py-2 text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" title={COA_COLUMNS[3].tooltip}>
                  {at.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </td>
              <td className="py-2 px-3 align-top overflow-hidden">
                <select value={form.compliance_or_fwa} onChange={(e) => setForm((f) => ({ ...f, compliance_or_fwa: e.target.value }))} className="w-full rounded-xl border border-slate-200 px-2.5 py-2 text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" title={COA_COLUMNS[4].tooltip}>
                  {cf.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </td>
              <td className="py-2 px-3 align-top overflow-hidden">
                <select value={form.activity_frequency} onChange={(e) => setForm((f) => ({ ...f, activity_frequency: e.target.value }))} className="w-full rounded-xl border border-slate-200 px-2.5 py-2 text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" title={COA_COLUMNS[5].tooltip}>
                  {af.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </td>
              <td className="py-2 px-3 align-top overflow-hidden">
                <select value={form.activity_quarter} onChange={(e) => setForm((f) => ({ ...f, activity_quarter: e.target.value }))} className="w-full rounded-xl border border-slate-200 px-2.5 py-2 text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" title={COA_COLUMNS[6].tooltip}>
                  {aq.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </td>
              <td className="py-2 px-3 align-top">
                <textarea maxLength={200} placeholder="Rationale (Enter for new line)" value={form.activity_rationale} onChange={(e) => setForm((f) => ({ ...f, activity_rationale: e.target.value }))} rows={2} className="w-full rounded-xl border border-slate-200 px-2.5 py-2 text-sm bg-white shadow-sm resize-y min-h-[4rem] focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" title={COA_COLUMNS[7].tooltip} required />
              </td>
              <td className="py-2 px-3 align-top">
                <textarea maxLength={400} placeholder="Description (Enter for new line)" value={form.activity_description} onChange={(e) => setForm((f) => ({ ...f, activity_description: e.target.value }))} rows={2} className="w-full rounded-xl border border-slate-200 px-2.5 py-2 text-sm bg-white shadow-sm resize-y min-h-[4rem] focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" title={COA_COLUMNS[8].tooltip} required />
              </td>
              <td className="py-2 px-3 align-top">
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="YYYY/MM/DD"
                  value={form.activity_start_date}
                  onChange={(e) => setForm((f) => ({ ...f, activity_start_date: e.target.value }))}
                  className="w-full rounded-xl border border-slate-200 px-2.5 py-2 text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                  title={`${COA_COLUMNS[9].tooltip} Format: YYYY/MM/DD`}
                  required
                />
              </td>
              <td className="py-2 px-3 align-top text-slate-500 italic" title={COA_COLUMNS[10].tooltip}>TBD — add when editing</td>
              <td className="py-2 px-3 align-top text-slate-500 italic" title={COA_COLUMNS[11].tooltip}>TBD — add when editing</td>
              <td className="py-2 px-3 align-top text-slate-500 italic" title={COA_COLUMNS[12].tooltip}>TBD — add when editing</td>
              <td className="py-2 px-3 align-top">
                <textarea placeholder="TBD or metric/threshold; audit: corrective actions" value={form.remediation_activities || ''} onChange={(e) => setForm((f) => ({ ...f, remediation_activities: e.target.value }))} rows={2} className="w-full rounded-xl border border-slate-200 px-2.5 py-2 text-sm bg-white shadow-sm resize-y min-h-[4rem] focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" title={COA_COLUMNS[13].tooltip} />
              </td>
              <td className="py-2 px-3 align-top overflow-hidden" title={COA_COLUMNS[14].tooltip}>
                <div className="flex gap-1.5">
                  <input type="number" min="0" step="0.1" placeholder="95" value={form.benchmark ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, benchmark: e.target.value }))}
                    className="w-full min-w-0 rounded-xl border border-slate-200 px-2.5 py-2 text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" />
                  <select value={form.benchmark_unit || 'percent'}
                    onChange={(e) => setForm((f) => ({ ...f, benchmark_unit: e.target.value }))}
                    className="rounded-xl border border-slate-200 px-1.5 py-2 text-xs bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400">
                    <option value="percent">%</option>
                    <option value="seconds">sec</option>
                    <option value="minutes">min</option>
                    <option value="hours">hrs</option>
                    <option value="days">days</option>
                    <option value="count">#</option>
                  </select>
                </div>
                <select value={form.benchmark_direction || 'higher'}
                  onChange={(e) => setForm((f) => ({ ...f, benchmark_direction: e.target.value }))}
                  className="mt-1.5 w-full rounded-xl border border-slate-200 px-2 py-1.5 text-xs bg-white shadow-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400">
                  <option value="higher">At or above passes</option>
                  <option value="lower">At or below passes</option>
                </select>
              </td>
              {/* Measurements has no input while adding: results arrive later,
                  from a sync or from the entry box on the row. The cell must
                  still exist or every column after it sits under the wrong
                  heading, which is how this table has been rendering. */}
              <td className="py-2 px-3 align-top text-slate-500 italic" title={COA_COLUMNS[15].tooltip}>Added with results</td>
              <td className="py-2 px-3 align-top overflow-hidden">
                <select value={form.corrective_action_required} onChange={(e) => setForm((f) => ({ ...f, corrective_action_required: e.target.value }))} className="w-full rounded-xl border border-slate-200 px-2.5 py-2 text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" title={COA_COLUMNS[15].tooltip}>
                  <option value="N">N</option>
                  <option value="Y">Y</option>
                  <option value="TBD">TBD</option>
                </select>
              </td>
              <td className="py-2 px-3 align-top">
                <input type="text" maxLength={50} placeholder="Y/N + name" value={form.activity_results_shared} onChange={(e) => setForm((f) => ({ ...f, activity_results_shared: e.target.value }))} className="w-full rounded-xl border border-slate-200 px-2.5 py-2 text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" title={COA_COLUMNS[15].tooltip} />
              </td>
              <td className="py-2 px-3 align-top">
                <textarea placeholder="CFR (Enter for new line)" value={form.cfr_references} onChange={(e) => setForm((f) => ({ ...f, cfr_references: e.target.value }))} rows={2} className="w-full rounded-xl border border-slate-200 px-2.5 py-2 text-sm bg-white shadow-sm resize-y min-h-[4rem] focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" title={COA_COLUMNS[16].tooltip} />
              </td>
              <td className="py-2 px-3 align-top">
                <textarea placeholder="Chapter (Enter for new line)" value={form.chapter_references} onChange={(e) => setForm((f) => ({ ...f, chapter_references: e.target.value }))} rows={2} className="w-full rounded-xl border border-slate-200 px-2.5 py-2 text-sm bg-white shadow-sm resize-y min-h-[4rem] focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" title={COA_COLUMNS[17].tooltip} />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="px-5 py-3 border-t border-slate-200/60 bg-slate-50/50">
        <label className="text-slate-600 text-sm font-medium block mb-2">Assigned users (for rebuttal requests — up to 3 per department)</label>
        {assignableUsers.length === 0 && form.department_ids.length > 0 && <p className="text-xs text-slate-400 mb-1">Loading users for selected departments…</p>}
        {assignableUsers.length === 0 && form.department_ids.length === 0 && <p className="text-xs text-slate-400 mb-1">Select departments above to choose assignees.</p>}
        <div className="flex flex-wrap gap-3">
          {assignableUsers.map((u) => (
            <label key={u.id} className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={(form.assignee_user_ids || []).includes(u.id)}
                onChange={() => setForm((f) => {
                  const ids = f.assignee_user_ids || [];
                  return { ...f, assignee_user_ids: ids.includes(u.id) ? ids.filter((x) => x !== u.id) : [...ids, u.id] };
                })}
                className="rounded"
              />
              {[u.name, u.email].filter(Boolean).join(' · ')}
            </label>
          ))}
        </div>
      </div>
      <div className="px-5 py-4 border-t border-slate-200/60 flex gap-3">
        <button
          type="submit"
          disabled={saving || impersonating}
          title={impersonating ? 'Read-only during impersonation' : ''}
          className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white font-medium shadow-sm shadow-orange-500/25 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {saving ? 'Saving...' : 'Save activity'}
        </button>
        <button type="button" onClick={onCancel} className="px-5 py-2.5 rounded-xl bg-slate-100 text-slate-700 border border-slate-200 font-medium hover:bg-slate-200 transition-colors">Cancel</button>
      </div>
    </form>
  );
}

function ActivityCard({ activity, isSelected, onSelect, onRefresh, templates, departments }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const isCompleted = activity.status === 'completed';

  const handleSave = () => {
    if (!form) return;
    const payload = { ...form };
    if (Array.isArray(form.department_ids)) payload.department_ids = form.department_ids;
    else if (Array.isArray(form.departments)) payload.department_ids = form.departments.map((d) => d.id);
    if (Array.isArray(form.assignee_user_ids)) payload.assignee_user_ids = form.assignee_user_ids;
    fetch(`${API}/api/coa/activities/${activity.id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then((r) => r.json())
      .then((d) => { if (d.success) { setEditing(false); onRefresh(); } })
      .catch(() => {});
  };

  const handleComplete = () => {
    if (!window.confirm('Mark this activity as completed? Deficiencies will be locked.')) return;
    fetch(`${API}/api/coa/activities/${activity.id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'completed' })
    })
      .then((r) => r.json())
      .then((d) => { if (d.success) onRefresh(); });
  };

  if (editing && form) {
    const at = templates.activity_type || ['Auditing', 'Monitoring', 'Investigations'];
    const af = templates.activity_frequency || ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Ad-hoc'];
    const aq = templates.activity_quarter || ['Q1', 'Q2', 'Q3', 'Q4'];
    const deptIds = Array.isArray(form.department_ids) ? form.department_ids : (form.departments || []).map((d) => d.id);
    const toggleDept = (id) => {
      setForm((f) => {
        const ids = Array.isArray(f.department_ids) ? f.department_ids : (f.departments || []).map((d) => d.id);
        return { ...f, department_ids: ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id] };
      });
    };
    return (
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-4">
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <label className="text-slate-500">Component</label>
            <input value={form.component} onChange={(e) => setForm((f) => ({ ...f, component: e.target.value }))} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" />
          </div>
          <div>
            <label className="text-slate-500">Entity name</label>
            <input value={form.entity_name || ''} onChange={(e) => setForm((f) => ({ ...f, entity_name: e.target.value }))} placeholder="FDR or internal dept" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" />
          </div>
          <div className="col-span-2">
            <label className="text-slate-500">Departments</label>
            <div className="flex flex-wrap gap-2 mt-0.5">
              {departments.map((d) => (
                <label key={d.id} className="flex items-center gap-1 text-xs">
                  <input type="checkbox" checked={deptIds.includes(d.id)} onChange={() => toggleDept(d.id)} className="rounded" />
                  {d.name}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="text-slate-500">Activity type</label>
            <select value={form.activity_type} onChange={(e) => setForm((f) => ({ ...f, activity_type: e.target.value }))} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400">
              {at.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="text-slate-500">Frequency</label>
            <select value={form.activity_frequency} onChange={(e) => setForm((f) => ({ ...f, activity_frequency: e.target.value }))} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400">
              {af.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="text-slate-500">Quarter</label>
            <select value={form.activity_quarter || ''} onChange={(e) => setForm((f) => ({ ...f, activity_quarter: e.target.value }))} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400">
              {aq.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="text-slate-500">Status</label>
            <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400">
              <option value="draft">Draft</option>
              <option value="in_progress">In progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
        <div className="mt-2 flex gap-2">
          <button type="button" onClick={handleSave} className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm font-medium shadow-sm shadow-orange-500/25 hover:opacity-90 transition-all">Save</button>
          <button type="button" onClick={() => { setEditing(false); setForm(null); }} className="px-4 py-2 rounded-xl bg-slate-100 text-slate-700 border border-slate-200 text-sm font-medium hover:bg-slate-200 transition-colors">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`rounded-2xl border shadow-sm p-4 cursor-pointer transition-all ${isSelected ? 'bg-orange-50/50 border-orange-200 ring-1 ring-orange-200' : 'bg-white border-slate-200/60 hover:bg-slate-50'}`}
      onClick={onSelect}
    >
      <div className="flex justify-between items-start">
        <div>
          <span className="font-medium text-slate-800">{activity.component}</span>
          {activity.entity_name && <span className="ml-2 text-slate-600 text-sm">({activity.entity_name})</span>}
          {(activity.departments && activity.departments.length > 0) && (
            <span className="ml-2 text-slate-500 text-xs">Depts: {activity.departments.map((d) => d.name).join(', ')}</span>
          )}
          {(activity.assignees && activity.assignees.length > 0) && (
            <span className="ml-2 text-slate-500 text-xs">Assignees: {activity.assignees.map((u) => [u.name, u.email].filter(Boolean).join(' · ')).join(', ')}</span>
          )}
          <span className="ml-2 text-slate-500 text-sm">{activity.activity_type} · {activity.activity_frequency}{activity.activity_quarter ? ` · ${activity.activity_quarter}` : ''}</span>
          {activity.cfr_references && <span className="ml-2 text-xs text-slate-400">CFR: {activity.cfr_references}</span>}
          <p className="text-sm text-slate-600 mt-1 line-clamp-1">{activity.activity_description}</p>
          <p className="text-xs text-slate-400 mt-0.5">{activity.activity_start_date} → {activity.activity_completion_date || 'TBD'} · Deficiencies: {activity.number_of_deficiencies}</p>
          {activity.created_cap_id && <p className="text-xs text-orange-600 mt-0.5">CAP: {activity.created_cap_id}</p>}
        </div>
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          {!isCompleted && (
            <>
              <button type="button" onClick={() => { setForm({ ...activity, department_ids: (activity.departments || []).map((d) => d.id), assignee_user_ids: (activity.assignees || []).map((u) => u.id) }); setEditing(true); }} className="px-3 py-1.5 rounded-xl bg-slate-100 text-slate-700 border border-slate-200 text-xs font-medium hover:bg-slate-200 transition-colors">Edit</button>
              <button type="button" onClick={handleComplete} className="px-3 py-1.5 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xs font-medium shadow-sm shadow-orange-500/25 hover:opacity-90 transition-all">Complete</button>
            </>
          )}
          {isCompleted && <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-500 text-xs font-medium">Locked</span>}
        </div>
      </div>
    </div>
  );
}

function ResultsEntryPanel({ activity, user, isCoaCompliance, onRequestResults, requestingResultsId, onRefresh }) {
  const [form, setForm] = useState({
    remediation_activities: activity.remediation_activities || '',
    activity_completion_date: isoToCmsDate(activity.activity_completion_date) || '',
    number_of_deficiencies: activity.number_of_deficiencies ?? '',
    description_of_deficiencies: activity.description_of_deficiencies || ''
  });
  const [saving, setSaving] = useState(false);
  const isAssignee = user?.id && (activity.assignees || []).some((a) => a.id === user.id);

  useEffect(() => {
    setForm({
      remediation_activities: activity.remediation_activities || '',
      activity_completion_date: isoToCmsDate(activity.activity_completion_date) || '',
      number_of_deficiencies: activity.number_of_deficiencies ?? '',
      description_of_deficiencies: activity.description_of_deficiencies || ''
    });
  }, [activity.id, activity.remediation_activities, activity.activity_completion_date, activity.number_of_deficiencies, activity.description_of_deficiencies]);

  const handleSave = () => {
    const completionIso = form.activity_completion_date ? cmsToIsoDate(form.activity_completion_date) : null;
    if (form.activity_completion_date && !completionIso) {
      alert('Completion date must be YYYY/MM/DD format.');
      return;
    }
    const numDef = form.number_of_deficiencies === '' ? null : (() => { const v = parseInt(form.number_of_deficiencies, 10); return Number.isNaN(v) ? null : v; })();
    setSaving(true);
    fetch(`${API}/api/coa/activities/${activity.id}/results`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        remediation_activities: form.remediation_activities || null,
        activity_completion_date: completionIso,
        number_of_deficiencies: numDef,
        description_of_deficiencies: form.description_of_deficiencies || null
      })
    })
      .then((r) => r.json())
      .then((d) => {
        setSaving(false);
        if (d.success) onRefresh();
        else alert(d.message || 'Failed to save results');
      })
      .catch(() => setSaving(false));
  };

  return (
    <div className="mt-6 bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6">
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <h3 className="font-medium text-slate-800">Remediation activities — {activity.component}</h3>
        {isCoaCompliance && onRequestResults && (
          <button
            type="button"
            onClick={() => onRequestResults(activity.id)}
            disabled={requestingResultsId === activity.id}
            className="text-amber-600 hover:text-amber-800 text-sm font-medium disabled:opacity-50"
          >
            {requestingResultsId === activity.id ? 'Sending…' : 'Request results from assignees'}
          </button>
        )}
      </div>
      <p className="text-sm text-slate-600 mb-4">
        For Monitoring/SIU: compliance can request assignees to enter results. Assignees enter metric, threshold, or remediation activities.
      </p>
      {isAssignee ? (
        <div className="space-y-4">
          <div>
            <label className="text-slate-500 block mb-1">Remediation activities (metric, threshold, or corrective actions)</label>
            <textarea
              value={form.remediation_activities}
              onChange={(e) => setForm((f) => ({ ...f, remediation_activities: e.target.value }))}
              placeholder="e.g. Metric: claims accuracy; threshold: 95%"
              rows={3}
              className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm resize-y min-h-[5rem] focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-slate-500 block mb-1">Completion date (YYYY/MM/DD)</label>
              <input
                type="text"
                placeholder="YYYY/MM/DD"
                value={form.activity_completion_date}
                onChange={(e) => setForm((f) => ({ ...f, activity_completion_date: e.target.value }))}
                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
              />
            </div>
            <div>
              <label className="text-slate-500 block mb-1">Number of deficiencies</label>
              <input
                type="number"
                min={0}
                value={form.number_of_deficiencies}
                onChange={(e) => setForm((f) => ({ ...f, number_of_deficiencies: e.target.value }))}
                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
              />
            </div>
          </div>
          <div>
            <label className="text-slate-500 block mb-1">Description of deficiencies</label>
            <textarea
              value={form.description_of_deficiencies}
              onChange={(e) => setForm((f) => ({ ...f, description_of_deficiencies: e.target.value }))}
              placeholder="TBD or NA"
              rows={2}
              className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
            />
          </div>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white font-medium shadow-sm shadow-orange-500/25 hover:opacity-90 disabled:opacity-50 transition-all"
          >
            {saving ? 'Saving…' : 'Save results'}
          </button>
        </div>
      ) : (
        <div className="text-slate-500 text-sm">
          {activity.remediation_activities ? (
            <p className="whitespace-pre-line">{activity.remediation_activities}</p>
          ) : (
            <p>No results entered yet. Compliance can request assignees to enter results.</p>
          )}
          {activity.results_requested_at && (
            <p className="mt-2 text-amber-700 text-xs">Results requested {isoToCmsDate(activity.results_requested_at)}</p>
          )}
        </div>
      )}
    </div>
  );
}

function DeficienciesPanel({ activity, deficiencies, onRefresh, onCapCreated, onClose, isCoaCompliance = true, onViewReport, onDownloadReport, onRequestRebuttal, requestingId }) {
  const [newDesc, setNewDesc] = useState('');
  const [newCfr, setNewCfr] = useState('');
  const [newChapter, setNewChapter] = useState('');
  const [rebuttalId, setRebuttalId] = useState(null);
  const [rebuttalText, setRebuttalText] = useState('');
  const [creatingCap, setCreatingCap] = useState(false);
  const [capError, setCapError] = useState('');
  const isCompleted = activity.status === 'completed';
  const canCreateCap = isCoaCompliance && isCompleted && deficiencies.length >= 1 && !activity.created_cap_id;

  const addDeficiency = () => {
    if (!newDesc.trim()) return;
    fetch(`${API}/api/coa/activities/${activity.id}/deficiencies`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ description: newDesc.trim(), cfr_references: newCfr.trim() || null, chapter_references: newChapter.trim() || null })
    })
      .then((r) => r.json())
      .then((d) => { if (d.success) { setNewDesc(''); setNewCfr(''); setNewChapter(''); onRefresh(); } });
  };

  const saveRebuttal = (defId) => {
    fetch(`${API}/api/coa/deficiencies/${defId}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rebuttal_text: rebuttalText })
    })
      .then((r) => r.json())
      .then((d) => { if (d.success) { setRebuttalId(null); setRebuttalText(''); onRefresh(); } });
  };

  // CAP now lives in-Core under /app (selectedApp='cap', cap_id deep-linked
  // via query string). The legacy https://cap.precisioncompliancegroup.com URL
  // is no longer used.
  const openCapDeepLink = (capId) => {
    const target = `/app?app=cap&cap_id=${encodeURIComponent(capId)}`;
    window.location.href = target;
  };

  const handleInitiateCap = () => {
    if (!canCreateCap || creatingCap) return;
    if (!window.confirm('Create a corrective action plan from this audit activity? The deficiency details and assignees will carry over.')) return;
    setCreatingCap(true);
    setCapError('');
    fetch(`${API}/api/coa/activities/${activity.id}/create-cap`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    })
      .then((r) => r.json())
      .then((d) => {
        setCreatingCap(false);
        if (d.success && d.cap_id) {
          onCapCreated?.();
          if (window.confirm('CAP created. Open it now to add the why-chain and milestones?')) openCapDeepLink(d.cap_id);
        } else {
          setCapError(d.message || 'Failed to create CAP');
        }
      })
      .catch(() => {
        setCreatingCap(false);
        setCapError('Request failed');
      });
  };

  const isAuditingCompleted = activity.activity_type === 'Auditing' && activity.status === 'completed';

  return (
    <div className="mt-6 bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6">
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <h3 className="font-medium text-slate-800">Deficiencies — {activity.component}</h3>
        <div className="flex items-center gap-2">
          {isAuditingCompleted && onViewReport && onDownloadReport && (
            <>
              <button type="button" onClick={() => onViewReport(activity.id)} className="text-orange-600 hover:text-orange-700 text-sm font-medium">View report</button>
              <button type="button" onClick={() => onDownloadReport(activity.id, 'pdf')} className="text-slate-600 hover:text-slate-800 text-sm">Download PDF</button>
              <button type="button" onClick={() => onDownloadReport(activity.id, 'docx')} className="text-slate-600 hover:text-slate-800 text-sm">Download Word</button>
              {isCoaCompliance && onRequestRebuttal && (
                <button type="button" onClick={() => onRequestRebuttal(activity.id)} disabled={requestingId === activity.id} className="text-amber-600 hover:text-amber-800 text-sm font-medium disabled:opacity-50">
                  {requestingId === activity.id ? 'Sending…' : 'Request rebuttal'}
                </button>
              )}
            </>
          )}
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-700 text-sm">Close</button>
        </div>
      </div>

      {activity.created_cap_id && (
        <div className="mb-4 p-4 rounded-xl bg-orange-50 border border-orange-200 text-orange-800 text-sm">
          <p className="font-medium mb-1">CAP created from this activity</p>
          <p className="mb-3">Open it to add the why-chain, milestones, and evidence.</p>
          <button
            type="button"
            onClick={() => openCapDeepLink(activity.created_cap_id)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm font-medium hover:opacity-90 transition-all"
          >
            Open the CAP
          </button>
        </div>
      )}
      {canCreateCap && (
        <div className="mb-4 flex items-center gap-2">
          <button
            type="button"
            onClick={handleInitiateCap}
            disabled={creatingCap}
            className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white font-medium shadow-sm shadow-orange-500/25 hover:opacity-90 disabled:opacity-50 transition-all"
          >
            {creatingCap ? 'Creating CAP…' : 'Initiate CAP'}
          </button>
          <span className="text-slate-500 text-xs">Create a CAP in the CAP app from this activity and its deficiencies.</span>
        </div>
      )}
      {capError && <div className="mb-4 p-3 rounded-xl bg-red-50 text-red-700 ring-1 ring-red-200 text-sm">{capError}</div>}

      {isCoaCompliance && (
        <div className="flex flex-col gap-3 mb-4">
          <div className="flex gap-2 flex-wrap items-start">
            <textarea
              placeholder="Add deficiency description (Enter for new line)"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              rows={4}
              className="flex-1 min-w-[200px] rounded-xl border border-slate-200 px-4 py-2.5 text-sm resize-y min-h-[6rem] focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
            />
            <textarea
              placeholder="CFR references (optional)"
              value={newCfr}
              onChange={(e) => setNewCfr(e.target.value)}
              rows={2}
              className="w-44 rounded-xl border border-slate-200 px-4 py-2.5 text-sm resize-y min-h-[4rem] focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
            />
            <textarea
              placeholder="Chapter refs (optional)"
              value={newChapter}
              onChange={(e) => setNewChapter(e.target.value)}
              rows={2}
              className="w-40 rounded-xl border border-slate-200 px-4 py-2.5 text-sm resize-y min-h-[4rem] focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
            />
            <button type="button" onClick={addDeficiency} className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm font-medium shadow-sm shadow-orange-500/25 hover:opacity-90 self-end transition-all">Add</button>
          </div>
          {isCompleted && <span className="text-slate-500 text-xs">Compliance may add findings after rebuttal.</span>}
        </div>
      )}

      {deficiencies.length === 0 && <p className="text-slate-500 text-sm">No deficiencies recorded.</p>}
      <ul className="space-y-3">
        {deficiencies.map((d) => (
          <li key={d.id} className="border border-slate-200/60 rounded-xl p-3 bg-white">
            <p className="text-sm text-slate-800 whitespace-pre-line">{d.description}</p>
            {(d.cfr_references || d.chapter_references) && (
              <p className="text-xs text-slate-500 mt-0.5 whitespace-pre-line">
                {d.cfr_references && <>CFR: {d.cfr_references}</>}
                {d.cfr_references && d.chapter_references && ' · '}
                {d.chapter_references && <>Chapter: {d.chapter_references}</>}
              </p>
            )}
            {d.rebuttal_text ? (
              <div className="mt-2 pl-3 border-l-2 border-slate-200">
                <p className="text-xs text-slate-500">Rebuttal</p>
                <p className="text-sm text-slate-700 whitespace-pre-line">{d.rebuttal_text}</p>
              </div>
            ) : (rebuttalId === d.id ? (
              <div className="mt-2">
                <textarea value={rebuttalText} onChange={(e) => setRebuttalText(e.target.value)} placeholder="Owner/auditee rebuttal (Enter for new line)" className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm resize-y min-h-[4rem] focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" rows={3} />
                <button type="button" onClick={() => saveRebuttal(d.id)} className="mt-1 px-3 py-1.5 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xs font-medium shadow-sm shadow-orange-500/25 hover:opacity-90 transition-all">Save rebuttal</button>
                <button type="button" onClick={() => { setRebuttalId(null); setRebuttalText(''); }} className="ml-1 px-2 py-1 rounded-xl bg-slate-100 text-slate-700 border border-slate-200 text-xs hover:bg-slate-200 transition-colors">Cancel</button>
              </div>
            ) : (!isCompleted || !isCoaCompliance) && (
              <button type="button" onClick={() => setRebuttalId(d.id)} className="mt-1 text-orange-600 font-medium hover:text-orange-700 text-xs">Add rebuttal</button>
            ))}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// COAAuditAgentActivity — polls /api/agent-progress for the COA audit
// agent that generates the audit draft report for a given activity.
// Mirrors the OracleHome DocumentAgentActivity pattern.
// ---------------------------------------------------------------------------
function COAAuditAgentActivity({ activityId }) {
  const { events, status } = useAgentProgress('coa_audit', activityId);
  if (!events || events.length === 0) return null;
  return <AgentThinkingPanel events={events} status={status} title="Activity" />;
}
